/**
 * Endoscopy specialty shell — three-column workstation:
 *
 *   ┌────────────┬────────────────────────────────┬──────────────┐
 *   │ Case tray  │       Video viewport           │ Report panel │
 *   │ (left)     │                                │ (right)      │
 *   │            │  ┌──────────────────────────┐  │  Findings    │
 *   │ case info  │  │     <video>              │  │  Anatomy tbl │
 *   │ procedure  │  │                          │  │  Stills grid │
 *   │ patient    │  │   ⏯  ⏮ ⏭  0.5 1 2 4×    │  │              │
 *   │            │  │   timeline ▶━━●━━━●━━━▶  │  │  [PDF]       │
 *   │            │  │   anatomy spans ▓▓▓▓▓    │  │  [Finalize]  │
 *   │            │  └──────────────────────────┘  │              │
 *   └────────────┴────────────────────────────────┴──────────────┘
 *
 * Video is a real HTMLVideoElement fed from the DICOM instance's
 * encapsulated Pixel Data (MPEG-4/H.264). For the sandbox path the
 * DICOM wraps an MP4 container verbatim so a Blob URL is sufficient.
 *
 * Features:
 *   - Play/pause, step, 0.5×/1×/2×/4× speed, timeline scrubber.
 *   - Anatomy-span labelling — hold L at start, Shift+L at end to
 *     commit; picks a label from the taxonomy dropdown.
 *   - ROI tools on the paused frame — rectangle, point.
 *   - Capture-still button (spacebar hit while paused).
 *   - Report-drafting panel: structured sections (Procedure · Findings
 *     · Diagnosis · Complications · Recommendations), auto-populated
 *     by anatomy-span summary + captured-still thumbnails; one-click
 *     PDF export via `window.print`-style inline-SVG render.
 *
 * Built to match the spec in docs/development/22-endopod-vaultpod-
 * integration.md §5.
 */
import { batch, createEffect, createMemo, createResource, createSignal, For, Index, onCleanup, Show } from "solid-js";
import { createStore, produce } from "solid-js/store";
import type { StudyInstance } from "@viewer/api/client";
import { auth } from "@viewer/auth/store";
import {
  anatomyForProcedure, guessProcedure, lesionsForProcedure, colorForSpan,
  PROCEDURE_LABEL, PROCEDURE_TYPES,
  type AnatomyLabel, type LesionLabel, type ProcedureType,
} from "../endoscopy/taxonomy";
import {
  IconAnatomy, IconLesion, IconCamera, IconPlay, IconPause,
  IconStepBack, IconStepFwd, IconBbox, IconKeypoint, IconTrash, IconEdit,
  IconSave, IconSign,
  IconChevronRight, IconCase, IconScope, IconSlide,
} from "@viewer/ui/Icons";
import { JSX } from "solid-js";
import {
  fetchReport, saveReport, finalizeReport, amendReport,
  type EndoLabelWire, type EndoStatus,
} from "../endoscopy/reportApi";
import { groupByseries, instanceKind, type SeriesGroup } from "../endoscopy/sopClass";
import { StillsView } from "../endoscopy/StillsView";
import { loadStudyImageDocs } from "../endoscopy/imageAnnoApi";
import { buildAutoDraft, autoDraftNarrative, type AutoDraft } from "../endoscopy/autoDraft";
import { profile } from "../settings/profile";
import { brandedAlert } from "../app/branded-dialog";

export interface EndoscopyShellProps {
  studyUid: string;
  patientName?: string;
  patientId?: string;
  /** Initial procedure guess from the DICOM description — overridable
   *  via the in-shell picker. Full set in `taxonomy.PROCEDURE_TYPES`. */
  procedure?: ProcedureType;
  instances: StudyInstance[];
  activeInstance?: StudyInstance;
  onPickInstance?: (uid: string) => void;
}

interface AnatomySpan {
  id: string;
  labelId: string;
  labelName: string;
  /** Seconds from video start. */
  tStart: number;
  tEnd: number;
  color: string;
}

interface ROI {
  id: string;
  kind: "rect" | "point";
  /** Frame time in seconds when ROI was drawn. */
  frameTime: number;
  lesionId: string;
  lesionName: string;
  color: string;
  /** Normalised coords 0..1 relative to video box. */
  x: number;
  y: number;
  w?: number;
  h?: number;
  note?: string;
  /** Whether this ROI is rendered into the export PDF. Mirrors the
   *  CapturedStill.include flag so the reader can curate which ROIs
   *  appear in the final report (some are mid-procedure work product
   *  the reader doesn't want in the official record). Default true. */
  include?: boolean;
}

/** A short, stable, human-usable case identifier derived from the
 *  study UID. Always available — so an anonymised case (patient
 *  "Anonymous", MRN "Unknown") can still be named on the phone or in
 *  a worklist. The full UID is kept as the element's title for the
 *  rare collision. */
function caseId(studyUid: string): string {
  const digits = (studyUid || "").replace(/\D/g, "");
  const tail = (digits || studyUid || "").slice(-8);
  return tail ? `C-${tail}` : "C-unknown";
}

interface CapturedStill {
  id: string;
  frameTime: number;
  /** data URL of the paused frame. */
  dataUrl: string;
  captureAt: number;
  include: boolean;   // goes into PDF
  /** Display label — seeded from the active mode's preset at capture
   *  time (Anatomy → landmark, Lesion → lesion) and freely editable
   *  via the inline editor. One unified field, no separate caption. */
  label: string;
  /** Which vocabulary seeded the label. Determines which preset
   *  dropdown the editor surfaces for quick changes. Free-typed
   *  labels keep their original `kind` so the reader can still see
   *  "this one was captured in lesion mode". */
  kind: "anatomy" | "lesion";
  /** Preset ID if the current label was picked from a dropdown
   *  (not free-typed). Cleared when the user types a custom value. */
  presetId?: string;
  /** Colour chip associated with the label, from the preset when
   *  available. Free-typed labels fall back to a neutral chip. */
  color?: string;
}

const SPEEDS = [0.5, 1, 2, 4];

/** Labeling mode. Two mutually-exclusive workflows:
 *   - `anatomy`: video keeps playing; user drags on the timeline to
 *     mark a [start, end] range (Audacity-style region selection)
 *     and picks an anatomical landmark to attach to it. The video
 *     overlay stays click-through so the viewer can scrub freely.
 *   - `lesion`: video auto-pauses on entering; user picks a lesion
 *     label and draws a bounding box (or drops a point) on the
 *     frozen frame. Drawing captures the current frame-time so the
 *     ROI surfaces as an overlay when playback returns to that
 *     neighbourhood. */
type Mode = "anatomy" | "lesion";
type LesionTool = "rect" | "point";

export function EndoscopyShell(p: EndoscopyShellProps) {
  let videoEl!: HTMLVideoElement;
  let hiddenCvs!: HTMLCanvasElement;
  let overlayCvs!: HTMLCanvasElement;
  let videoBox!: HTMLDivElement;
  let timelineEl!: HTMLDivElement;

  const [ready, setReady]   = createSignal(false);
  const [playing, setPlaying] = createSignal(false);
  const [speed, setSpeed]   = createSignal(1);
  const [curTime, setCurTime] = createSignal(0);
  const [duration, setDuration] = createSignal(0);
  const [mode, setMode] = createSignal<Mode>("anatomy");
  const [lesionTool, setLesionTool] = createSignal<LesionTool>("rect");
  const [activeAnatomy, setActiveAnatomy] = createSignal<AnatomyLabel | null>(null);
  const [activeLesion, setActiveLesion] = createSignal<LesionLabel | null>(null);
  const [spans, setSpans] = createSignal<AnatomySpan[]>([]);
  // ROIs use a solid-js store (NOT a plain signal), specifically so
  // edits keep the inline editor's <input> focused. With a signal +
  // `setROIs((xs) => xs.map((r) => r.id === id ? { ...r, lesionName: v } : r))`,
  // every keystroke creates a brand-new object reference for the
  // edited row; <For> keys on item identity, sees the change, tears
  // down + rebuilds the entire <li> including the <input>, and the
  // browser's focus drops to <body>. The result: the user types one
  // character and the cursor jumps out of the field.
  // The user reported this exact behaviour twice — once on the legacy
  // VaultPod build, once on this endopod fork — so this comment is
  // here to keep the next refactor from regressing it. The same trap
  // is documented in the operator's persistent-memory feedback file
  // `feedback_solid_reactivity_patterns.md` (Solid: don't snapshot
  // props into top-level consts) — sibling pattern.
  // Stills already use createStore + produce for the same reason; we
  // mirror that pattern here for consistency.
  const [rois, setROIs] = createStore<ROI[]>([]);
  // `stills` is a Solid store (not a plain signal) so per-field
  // updates via `setStills(i, "label", v)` are granular — the input
  // the user is typing into doesn't get torn down and remounted on
  // every keystroke, which was the focus-loss bug.
  const [stills, setStills] = createStore<CapturedStill[]>([]);

  // -------------------- Report persistence --------------------
  // Each logged-in user has their own per-study draft/finalized copy
  // on the server. On mount we fetch (with fallback to "seeded from
  // other user's finalized report" or blank template); afterwards
  // the Save Draft button pushes the current state back.
  const [reportStatus, setReportStatus] = createSignal<EndoStatus>("draft");
  const [seededFrom,   setSeededFrom]   = createSignal<string | null>(null);
  const [finalizedAt,  setFinalizedAt]  = createSignal<string | null>(null);
  const [finalizedBy,  setFinalizedBy]  = createSignal<string | null>(null);
  const [saveState,    setSaveState]    = createSignal<"idle" | "saving" | "saved" | "error">("idle");
  const [reportLoaded, setReportLoaded] = createSignal(false);

  // Readonly when status is `finalized` AND the user hasn't hit
  // "Amend" yet. Amended state is fully editable (same as draft).
  const readOnly = () => reportStatus() === "finalized";

  /** Snapshot the current client state into the shape the server
   *  persists. Called on every Save Draft + on the implicit save
   *  before Finalize. */
  function buildLabelsWire(): EndoLabelWire[] {
    const out: EndoLabelWire[] = [];
    for (const s of spans()) {
      out.push({
        kind: "anatomy_span",
        t_start: s.tStart, t_end: s.tEnd,
        label_id: s.labelId, label_name: s.labelName,
        label_kind: "anatomy", label_color: s.color,
      });
    }
    for (const r of rois) {
      out.push({
        kind: "lesion_roi",
        t_start: r.frameTime, t_end: null,
        label_id: r.lesionId, label_name: r.lesionName,
        label_kind: "lesion", label_color: r.color,
        roi_shape: r.kind,
        roi_x: r.x ?? null, roi_y: r.y ?? null,
        roi_w: r.w ?? null, roi_h: r.h ?? null,
      });
    }
    for (const s of stills) {
      out.push({
        kind: "captured_still",
        t_start: s.frameTime, t_end: null,
        label_id: s.presetId ?? null, label_name: s.label,
        label_kind: s.kind === "anatomy" ? "anatomy" : s.kind === "lesion" ? "lesion" : "custom",
        label_color: s.color ?? null,
        still_data_url: s.dataUrl,
        still_include: s.include,
      });
    }
    return out;
  }

  /** Rehydrate the client-side spans/rois/stills/body from a server
   *  response. Used on initial load and after save/finalize/amend.
   *  Writes are batched so downstream memos (anatomyList,
   *  lesionList, activeAnatomy default, activeLesion default) only
   *  recompute once per rehydrate — a naked sequence of setters
   *  inside a tracked context fires each tracker per write, which
   *  Solid's scheduler interprets as a pending cycle and blows the
   *  stack on deep specialty subtrees. */
  function applyServerReport(r: Awaited<ReturnType<typeof fetchReport>>) {
    if (!r) return;
    batch(() => {
      setReportStatus(r.status);
      setSeededFrom(r.seededFrom ?? null);
      setFinalizedAt(r.finalizedAt ?? null);
      setFinalizedBy(r.finalizedBy ?? null);
      if (r.procedureType && !manualProc()) setProcedure(r.procedureType as ProcedureType);
      setReport({
        procedureNotes:  String(r.body?.procedureNotes  ?? ""),
        findings:        String(r.body?.findings        ?? ""),
        diagnosis:       String(r.body?.diagnosis       ?? ""),
        complications:   String(r.body?.complications   ?? ""),
        recommendations: String(r.body?.recommendations ?? ""),
      });
      // Rehydrate the structured auto-draft (landmark coverage + photo
      // grid) stashed in the body under `_autodraft`.
      try {
        const raw = (r.body as any)?._autodraft;
        setAutoDraft(raw ? (typeof raw === "string" ? JSON.parse(raw) : raw) as AutoDraft : null);
      } catch { setAutoDraft(null); }
    // Labels.
    const newSpans: AnatomySpan[] = [];
    const newROIs:  ROI[] = [];
    const newStills: CapturedStill[] = [];
    for (const l of r.labels ?? []) {
      if (l.kind === "anatomy_span") {
        newSpans.push({
          id: crypto.randomUUID(),
          labelId: l.label_id ?? "",
          labelName: l.label_name ?? "",
          tStart: Number(l.t_start ?? 0),
          tEnd:   Number(l.t_end   ?? 0),
          color:  l.label_color ?? colorForSpan(newSpans.length),
        });
      } else if (l.kind === "lesion_roi") {
        newROIs.push({
          id: crypto.randomUUID(),
          kind: (l.roi_shape ?? "rect") as "rect" | "point",
          frameTime: Number(l.t_start ?? 0),
          lesionId: l.label_id ?? "",
          lesionName: l.label_name ?? "",
          color: l.label_color ?? "#f59e0b",
          x: Number(l.roi_x ?? 0),
          y: Number(l.roi_y ?? 0),
          w: l.roi_w == null ? undefined : Number(l.roi_w),
          h: l.roi_h == null ? undefined : Number(l.roi_h),
        });
      } else if (l.kind === "captured_still") {
        newStills.push({
          id: crypto.randomUUID(),
          frameTime: Number(l.t_start ?? 0),
          dataUrl: l.still_data_url ?? "",
          captureAt: Date.now(),
          include: l.still_include ?? true,
          label: l.label_name ?? "",
          kind: (l.label_kind === "lesion" ? "lesion" : "anatomy"),
          presetId: l.label_id ?? undefined,
          color:    l.label_color ?? undefined,
        });
      }
    }
      setSpans(newSpans);
      setROIs(newROIs);
      setStills(newStills);
    });
  }

  async function doSaveDraft() {
    setSaveState("saving");
    try {
      const r = await saveReport(p.studyUid, {
        procedureType: procedure(),
        body: reportBody(),
        labels: buildLabelsWire(),
      });
      applyServerReport(r);
      setSaveState("saved");
      setTimeout(() => { if (saveState() === "saved") setSaveState("idle"); }, 2000);
    } catch (e) {
      console.error("[endo/save]", e);
      setSaveState("error");
    }
  }
  async function doFinalize() {
    // Save current state first so the finalized row is complete.
    setSaveState("saving");
    try {
      await saveReport(p.studyUid, {
        procedureType: procedure(),
        body: reportBody(),
        labels: buildLabelsWire(),
      });
      const r = await finalizeReport(p.studyUid);
      applyServerReport(r);
      setSaveState("saved");
      setTimeout(() => { if (saveState() === "saved") setSaveState("idle"); }, 2000);
    } catch (e) {
      console.error("[endo/finalize]", e);
      setSaveState("error");
    }
  }
  async function doAmend() {
    try {
      const r = await amendReport(p.studyUid);
      applyServerReport(r);
    } catch (e) {
      console.error("[endo/amend]", e);
    }
  }

  // Initial load — when the study changes, fetch the user's report
  // (with fallback) and rehydrate. Runs once per study.
  createEffect(() => {
    const uid = p.studyUid;
    if (!uid) return;
    setReportLoaded(false);
    fetchReport(uid).then((r) => {
      applyServerReport(r);
      setReportLoaded(true);
    }).catch((e) => {
      console.warn("[endo/load]", e);
      setReportLoaded(true);  // let the user work offline
    });
  });
  const [panelOpen, setPanelOpen] = createSignal(true);
  const [trayOpen, setTrayOpen] = createSignal(true);
  // Folder-tree disclosure state — persisted so the reader returns
  // to the same expand/collapse layout. Mirrors pathopod's tree-state
  // pattern in PathologyShell.
  const [caseOpen, setCaseOpen] = createSignal(
    (() => { try { return localStorage.getItem("vpd.endo.tree.case") !== "0"; } catch { return true; } })());
  const [seriesTreeOpen, setSeriesTreeOpen] = createSignal(
    (() => { try { return localStorage.getItem("vpd.endo.tree.series") !== "0"; } catch { return true; } })());
  createEffect(() => { try { localStorage.setItem("vpd.endo.tree.case",   caseOpen()       ? "1" : "0"); } catch {} });
  createEffect(() => { try { localStorage.setItem("vpd.endo.tree.series", seriesTreeOpen() ? "1" : "0"); } catch {} });
  // Resizable right Report-Draft panel — width persisted per-user.
  // 280..720 px is the supported range; below 280 the form fields
  // start clipping ICD-10 lookup, above 720 the centre video panel
  // becomes too narrow on a 1280-wide laptop.
  const [reportWidth, setReportWidth] = createSignal(
    (() => { try { const v = parseInt(localStorage.getItem("vpd.endo.report.width") ?? "", 10); return Number.isFinite(v) ? Math.max(280, Math.min(720, v)) : 340; } catch { return 340; } })());
  createEffect(() => { try { localStorage.setItem("vpd.endo.report.width", String(reportWidth())); } catch {} });
  /** Audacity-style region selection on the timeline. While the user
   *  drags, both values are set; on release we commit the span and
   *  reset. Times are in seconds of video. */
  const [rangeSel, setRangeSel] = createSignal<{ t0: number; t1: number } | null>(null);
  const [report, setReport] = createSignal({
    procedureNotes: "",
    findings: "",
    diagnosis: "",
    complications: "",
    recommendations: "",
  });

  // ---- Automated report draft (from the per-image annotations) ----
  // Structured roll-up of what the reader labelled across the study's
  // stills: procedures, landmark coverage, findings, representative
  // photos. Persisted inside the report body under `_autodraft` so it
  // survives reload and feeds the PDF. See endoscopy/autoDraft.ts.
  const [autoDraft, setAutoDraft] = createSignal<AutoDraft | null>(null);
  const [autoBusy,  setAutoBusy]  = createSignal(false);

  /** Report body for save/finalize — the five editable sections plus
   *  the structured auto-draft (serialised under `_autodraft`). */
  function reportBody(): Record<string, string> {
    const d = autoDraft();
    return { ...report(), ...(d ? { _autodraft: JSON.stringify(d) } : {}) };
  }

  // instance_uid → StudyInstance, so the auto-draft photo refs resolve
  // to the same WADO JPEG URL the stills viewer uses.
  const instById = createMemo(() => {
    const m = new Map<string, StudyInstance>();
    for (const i of p.instances) m.set(i.instanceUid, i);
    return m;
  });
  function photoUrl(instanceUid: string): string {
    const inst = instById().get(instanceUid);
    if (!inst) return "";
    const tok = auth()?.accessToken ?? "";
    const qs = tok ? `?access_token=${encodeURIComponent(tok)}` : "";
    return `/viewer-api/image/studies/${inst.studyUid}/series/${inst.seriesUid}/instances/${inst.instanceUid}.jpg${qs}`;
  }

  async function doAutomateDraft() {
    setAutoBusy(true);
    try {
      const docs = await loadStudyImageDocs(p.studyUid);
      if (docs.length === 0) {
        await brandedAlert("No image labels yet — label the stills first (procedure, anatomical landmark, and any lesion on each image), then run Automate drafting.");
        return;
      }
      const draft = buildAutoDraft(docs);
      const narr = autoDraftNarrative(draft);
      batch(() => {
        setAutoDraft(draft);
        // Seed the editable narrative (leave diagnosis / complications /
        // recommendations for the clinician).
        setReport((r) => ({ ...r, procedureNotes: narr.procedureNotes, findings: narr.findings }));
        // If exactly one procedure was labelled, adopt it as the report's
        // procedure type (the dropdown). Both → leave the reader's choice.
        if (draft.procedures.length === 1) {
          setProcedure(draft.procedures[0] === "egd" ? "EGD" : "Colonoscopy");
          setManualProc(true);
        }
      });
    } catch (e) {
      console.error("[endo/autodraft]", e);
      await brandedAlert("Automate drafting failed — could not read the image annotations. Try again.");
    } finally {
      setAutoBusy(false);
    }
  }

  // Procedure can be overridden via the in-shell picker — defaults to
  // the prop (itself a DICOM-description guess). Held as state rather
  // than a prop-derived memo because the clinician is authoritative:
  // they might correct a modality-worklist mis-label.
  const [procedure, setProcedure] = createSignal<ProcedureType>(p.procedure ?? "EGD");
  // Keep the dropdown in sync if the *prop* changes (new study loaded
  // while the shell is mounted) but only for as long as the user
  // hasn't manually picked a different procedure.
  const [manualProc, setManualProc] = createSignal(false);
  createEffect(() => {
    if (!manualProc() && p.procedure) setProcedure(p.procedure);
  });
  const anatomyList = createMemo<AnatomyLabel[]>(() => anatomyForProcedure(procedure()));
  const lesionList  = createMemo<LesionLabel[]>(() => lesionsForProcedure(procedure()));

  // -------------------- Series grouping --------------------
  // EndoEdge bundles a procedure as one Study with two Series:
  //   · VL Endoscopic Image Storage   (1.2.840.10008.5.1.4.1.1.77.1.1)
  //     — JPEG snapshots, "Endoscopy Snapshots"
  //   · Video Endoscopic Image Storage (1.2.840.10008.5.1.4.1.1.77.1.1.1)
  //     — MPEG-4 clips, "Endoscopy Video"
  //
  // The shell switches its centre column between the StillsView and
  // the existing video viewport based on which series the active
  // instance lives in. The explorer tree on the left shows both
  // series with kind-distinct previews so the reader can flip between
  // them with one click.
  const seriesGroups = createMemo<SeriesGroup[]>(() => groupByseries(p.instances));
  const stillsGroup  = createMemo<SeriesGroup | undefined>(
    () => seriesGroups().find((g) => g.kind === "stills"));
  const videoGroup   = createMemo<SeriesGroup | undefined>(
    () => seriesGroups().find((g) => g.kind === "video"));
  // Active centre — derived from the SOP class of the active instance,
  // with a series-level fallback for instances whose SOP Class UID
  // came back empty (older dicomweb-pg builds, or any QIDO row that
  // skipped 0008,0016). The series itself classifies via
  // SeriesDescription ("Endoscopy Snapshots" / "Endoscopy Video")
  // so the centre still routes correctly even with no per-instance
  // SOP class. Without this fallback the video element stays
  // mounted on a stills instance and `/viewer-api/video/...{uid}.mp4`
  // 502s — the symptom from issue #4 user testing.
  const activeKind = createMemo<"stills" | "video" | "other">(() => {
    const inst = p.activeInstance;
    const k = instanceKind(inst);
    if (k !== "other") return k;
    if (inst) {
      const g = seriesGroups().find((s) => s.seriesUid === inst.seriesUid);
      if (g && g.kind !== "other") return g.kind;
    }
    // No active instance — prefer stills if the bundle contains them
    // (endoedge ships SeriesNumber=1 = Snapshots, so they're the
    // natural landing surface). Fall back to video for legacy
    // single-clip studies.
    if (stillsGroup()) return "stills";
    if (videoGroup())  return "video";
    return "other";
  });
  /** Pick the first instance in the given series, telling the parent
   *  shell to refocus. Used when the reader clicks a series group in
   *  the explorer tree. */
  function pickSeries(g: SeriesGroup) {
    const target = g.instances.find((x) => x.instanceUid === p.activeInstance?.instanceUid)
                 ?? g.instances[0];
    if (target) p.onPickInstance?.(target.instanceUid);
  }
  // Anatomy-span row being edited inline in the right panel.
  const [editingSpanId, setEditingSpanId] = createSignal<string | null>(null);
  // Captured-still being edited (label/custom caption) in the stills
  // grid. Only one at a time — clicking another row closes the open
  // one.
  const [editingStillId, setEditingStillId] = createSignal<string | null>(null);

  // -------------------- Video loading state --------------------
  // For a sandbox 25 s MP4 this all resolves sub-second, but the
  // UI is built so it works against arbitrarily long endoscopy
  // recordings (15 min – 1 h). Three signals the UI consumes:
  //
  //   fetchProgress   — 0 to 1 during the initial DICOM download
  //                     (before the <video> element even mounts).
  //                     Computed from a ReadableStream's byte count
  //                     against the response's Content-Length.
  //   bufferedRanges  — array of [start, end] in seconds, read from
  //                     HTMLMediaElement.buffered on every `progress`
  //                     event. Visualised as thin bars above the
  //                     timeline so the user sees which parts are
  //                     already decoded + playable.
  //   videoLoading    — true while the browser is fetching data it
  //                     doesn't yet have (`waiting`, `stalled`, or a
  //                     seek into an unbuffered region). Pill
  //                     overlay on the video surface + border halo
  //                     on the buffered bar.
  //
  // The streaming-friendly long-form story (range-requested MP4 or
  // HLS/DASH) is a server-side fork of this same UI: the signals
  // stay valid, only the resource loader changes.
  const [fetchProgress, setFetchProgress] = createSignal<number | null>(null);
  const [fetchTotalBytes, setFetchTotalBytes] = createSignal<number | null>(null);
  const [bufferedRanges, setBufferedRanges] = createSignal<Array<[number, number]>>([]);
  const [videoLoading, setVideoLoading] = createSignal(false);

  // Pick a sensible default anatomy + lesion label as soon as the
  // respective lists resolve for the current procedure. Also swap
  // the active label if the user changed procedure mid-session —
  // the old selection probably isn't valid under the new vocabulary
  // (e.g. "Caecum" when switching from Colonoscopy to Bronchoscopy).
  createEffect(() => {
    const list = anatomyList();
    if (!list.length) return;
    const cur = activeAnatomy();
    if (!cur || !list.some((a) => a.id === cur.id)) setActiveAnatomy(list[0]);
  });
  createEffect(() => {
    const list = lesionList();
    if (!list.length) return;
    const cur = activeLesion();
    if (!cur || !list.some((l) => l.id === cur.id)) setActiveLesion(list[0]);
  });

  // Auto-pause the video when the user enters Lesion mode — drawing
  // a bbox on a moving frame gives you a box that's already wrong by
  // the time you commit it. Auto-resume is deliberately *not* wired
  // on returning to Anatomy mode; the user may want to inspect the
  // same frame further.
  createEffect(() => {
    if (mode() === "lesion" && videoEl && !videoEl.paused) videoEl.pause();
  });

  // ---------------- Load video as Blob URL ----------------
  // WADO-RS returns the raw DICOM instance bytes (application/dicom).
  // For the sandbox, the encapsulated Pixel Data IS the MP4 container,
  // so we locate the fragment marker (0xFFFE 0xE000) + 4-byte length
  // and hand the slice to URL.createObjectURL as video/mp4.
  // ============================================================
  // Video load strategy — MediaSource Extensions preferred, blob
  // URL fallback.
  //
  // MSE lets the <video> element start playing as soon as enough
  // bytes to constitute a valid initialisation segment have been
  // appended, with subsequent bytes streaming into a SourceBuffer
  // while the user is already watching. That's the critical UX
  // win for 15 min – 1 h recordings: you don't wait for the whole
  // DICOM to download. Falls back to a pre-built blob URL when:
  //   - MSE isn't available (very old browser)
  //   - the MP4 isn't fragmented enough for SourceBuffer to accept
  //     (most of our pyav-produced sandbox clips aren't fMP4) — in
  //     which case MSE rejects the appendBuffer with a QUOTA or
  //     INVALID_STATE error and we retry via blob URL.
  //
  // Either way the download-progress UI (blue strip) and the
  // buffered-ranges UI (green strip) keep working — both are
  // driven off signals updated in the loader and off HTMLMedia-
  // Element events respectively.
  // ============================================================
  const MSE_CODECS = [
    'video/mp4; codecs="avc1.42E01E,mp4a.40.2"',
    'video/mp4; codecs="avc1.64001E,mp4a.40.2"',
    'video/mp4; codecs="avc1.42E01E"',
    'video/mp4; codecs="avc1.64001E"',
  ];
  /** Try to feed the given WADO instance into a MediaSource attached
   *  to a <video>. Returns true if MSE accepted the stream; false if
   *  the caller should fall back to a pre-built blob URL. */
  async function tryStreamMSE(url: string, token: string): Promise<boolean> {
    if (typeof (globalThis as unknown as { MediaSource?: unknown }).MediaSource === "undefined") return false;
    const mime = MSE_CODECS.find((c) => MediaSource.isTypeSupported(c));
    if (!mime || !videoEl) return false;
    const ms = new MediaSource();
    videoEl.src = URL.createObjectURL(ms);
    const accepted = await new Promise<boolean>((resolve) => {
      ms.addEventListener("sourceopen", async () => {
        let sb: SourceBuffer;
        try { sb = ms.addSourceBuffer(mime); }
        catch { resolve(false); return; }
        let rsp: Response;
        try {
          rsp = await fetch(url, {
            headers: { Accept: "application/dicom", Authorization: `Bearer ${token}` },
          });
          if (!rsp.ok || !rsp.body) { resolve(false); return; }
        } catch { resolve(false); return; }
        const cl = rsp.headers.get("content-length");
        const total = cl ? parseInt(cl, 10) : null;
        if (total) setFetchTotalBytes(total);
        const reader = rsp.body.getReader();
        let buf = new Uint8Array(0);
        let fragOff = -1;
        let appended = 0;
        let received = 0;
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;
          received += value.byteLength;
          if (total) setFetchProgress(Math.min(1, received / total));
          // Grow the accumulating DICOM buffer.
          const next = new Uint8Array(buf.length + value.length);
          next.set(buf);
          next.set(value, buf.length);
          buf = next;
          // First time we've got enough bytes to locate the MP4
          // fragment, remember its starting offset.
          if (fragOff < 0) {
            const ft = findFragment(buf);
            if (ft) fragOff = ft.offset;
          }
          if (fragOff >= 0) {
            const slice = buf.subarray(fragOff + appended, buf.length);
            if (slice.length > 0) {
              while (sb.updating) {
                await new Promise((r) => sb.addEventListener("updateend", r, { once: true }));
              }
              try {
                sb.appendBuffer(slice);
                appended += slice.length;
                await new Promise((r) => sb.addEventListener("updateend", r, { once: true }));
              } catch {
                // MSE rejected — not fMP4-compatible. Tear down
                // and let the caller use the blob fallback.
                try { ms.endOfStream("decode"); } catch { /* ok */ }
                resolve(false); return;
              }
            }
          }
        }
        setFetchProgress(1);
        try { if (ms.readyState === "open") ms.endOfStream(); } catch { /* ok */ }
        resolve(true);
      }, { once: true });
    });
    return accepted;
  }

  /** Fetch the whole DICOM, extract the MP4 fragment, and return a
   *  blob URL.  Always uses the chunked reader so the progress UI
   *  has something to show even when the upstream did not advertise
   *  a Content-Length (which is the common case once nginx-ingress
   *  switches to chunked transfer-encoding for streaming). */
  async function loadBlobUrl(url: string, token: string): Promise<string | null> {
    const rsp = await fetch(url, {
      headers: { Accept: "application/dicom", Authorization: `Bearer ${token}` },
    });
    if (!rsp.ok || !rsp.body) return null;
    const cl = rsp.headers.get("content-length");
    const total = cl ? parseInt(cl, 10) : null;
    if (total) setFetchTotalBytes(total);
    const reader = rsp.body.getReader();
    const chunks: Uint8Array[] = [];
    let received = 0;
    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      chunks.push(value);
      received += value.byteLength;
      // With a known total we report a 0..1 fraction, otherwise we
      // surface the absolute byte count via setFetchTotalBytes so
      // the UI can render "X MB downloaded" without a percentage.
      if (total) setFetchProgress(Math.min(1, received / total));
      else       setFetchTotalBytes(received);
    }
    const buf = new Uint8Array(received);
    let offset = 0;
    for (const c of chunks) { buf.set(c, offset); offset += c.byteLength; }
    const ft = findFragment(buf);
    const payload = ft ? buf.subarray(ft.offset, ft.offset + ft.length) : buf;
    setFetchProgress(1);
    return URL.createObjectURL(new Blob([payload], { type: "video/mp4" }));
  }

  // Server-extracted MP4 endpoint.  The viewer-api strips the DICOM
  // Pixel Data wrapper and serves `video/mp4` with `Accept-Ranges:
  // bytes` so the browser can natively progressive-play a faststart
  // MP4 — first frames appear after a few hundred KB instead of after
  // a 200+ MB DICOM finishes downloading.  The access token rides as
  // a query parameter because <video src=…> does not carry the
  // Authorization header on the implicit fetch.
  const [videoUrl] = createResource(
    () => p.activeInstance?.instanceUid,
    (uid) => {
      if (!uid) return null;
      const inst = p.instances.find((x) => x.instanceUid === uid);
      if (!inst) return null;
      // Don't ever hand a stills SOP to the video pipeline — the
      // server's MP4 extractor rejects a JPEG fragment with a 502
      // and the <video> element surfaces it as a confusing
      // "[vpd/endo-video] error 4 502: Bad Gateway" line. Belt-and
      // -braces with the parent Show that already unmounts the
      // <video> element when the active series is stills.
      if (instanceKind(inst) === "stills") return null;
      const token = auth()?.accessToken ?? "";
      setFetchProgress(0);
      setFetchTotalBytes(null);
      setBufferedRanges([]);
      const qs = token ? `?access_token=${encodeURIComponent(token)}` : "";
      return `/viewer-api/video/studies/${inst.studyUid}/series/${inst.seriesUid}/instances/${uid}.mp4${qs}`;
    },
  );

  // ---------------- Video event wiring ----------------
  function attachVideo(el: HTMLVideoElement) {
    videoEl = el;
    // Sample TimeRanges → simple [start,end] pairs. Called from
    // every event that can change the buffered set.
    const pushBuffered = () => {
      const arr: Array<[number, number]> = [];
      const b = el.buffered;
      for (let i = 0; i < b.length; i++) arr.push([b.start(i), b.end(i)]);
      setBufferedRanges(arr);
    };
    el.addEventListener("loadedmetadata", () => {
      setDuration(el.duration || 0);
      setReady(true);
      pushBuffered();
    });
    el.addEventListener("play",  () => { setPlaying(true);  setVideoLoading(false); });
    el.addEventListener("pause", () => setPlaying(false));
    el.addEventListener("timeupdate", () => {
      setCurTime(el.currentTime);
      drawOverlay();
    });
    // `progress` fires as more data is buffered. `seeking` fires
    // when `.currentTime` is set to a new value; if the target
    // isn't yet buffered the browser also fires `waiting`. We
    // surface a "loading" state for any of waiting/stalled/seeking
    // and clear it once `playing` or `canplay` resumes.
    el.addEventListener("progress", pushBuffered);
    el.addEventListener("seeking",  () => { setVideoLoading(true);  pushBuffered(); });
    el.addEventListener("seeked",   () => { setVideoLoading(false); pushBuffered(); });
    el.addEventListener("waiting",  () => setVideoLoading(true));
    el.addEventListener("stalled",  () => setVideoLoading(true));
    el.addEventListener("playing",  () => setVideoLoading(false));
    el.addEventListener("canplay",  () => { setVideoLoading(false); pushBuffered(); });
    el.addEventListener("canplaythrough", () => setVideoLoading(false));
    el.addEventListener("error", () => {
      const e = el.error;
      console.error("[vpd/endo-video] error",
        e ? `${e.code} ${e.message}` : "(no MediaError)");
      setVideoLoading(false);
    });
  }
  createEffect(() => { if (videoEl) videoEl.playbackRate = speed(); });

  // ---------------- Overlay draw (ROIs + anatomy bar ghost) ----------------
  //
  // Contrast plan (mirrors the radiology AnnotationLayer):
  //
  //   rect   — dark 4-px outer halo + 2-px coloured inner stroke,
  //            so the box reads on both pink mucosa and white
  //            specular highlights.
  //   point  — white 3-px outer ring + coloured solid dot; the
  //            ring gives separation from any coloured background.
  //   label  — pill: dark rounded rect with coloured left stripe
  //            + white text on top. Keeps the text legible even
  //            when the bbox sits on a saturated highlight.
  function drawOverlay() {
    if (!overlayCvs || !videoEl) return;
    const w = videoEl.clientWidth, h = videoEl.clientHeight;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cw = Math.max(1, Math.floor(w * dpr));
    const ch = Math.max(1, Math.floor(h * dpr));
    if (overlayCvs.width !== cw || overlayCvs.height !== ch) {
      overlayCvs.width = cw; overlayCvs.height = ch;
    }
    const ctx = overlayCvs.getContext("2d");
    if (!ctx) return;
    ctx.clearRect(0, 0, cw, ch);
    for (const r of rois) {
      if (Math.abs(r.frameTime - curTime()) > 2) continue;
      if (r.kind === "rect") {
        const x = r.x * cw, y = r.y * ch;
        const rw = (r.w ?? 0) * cw, rh = (r.h ?? 0) * ch;
        // Soft tint fill
        ctx.fillStyle = r.color + "22";
        ctx.fillRect(x, y, rw, rh);
        // Outer dark halo (renders against bright backgrounds)
        ctx.lineWidth   = 4 * dpr;
        ctx.strokeStyle = "rgba(2, 6, 23, 0.9)";
        ctx.strokeRect(x, y, rw, rh);
        // Inner coloured stroke
        ctx.lineWidth   = 2 * dpr;
        ctx.strokeStyle = r.color;
        ctx.strokeRect(x, y, rw, rh);
        drawLabelPill(ctx, x, y - 4 * dpr, r.lesionName, r.color, dpr);
      } else {
        const x = r.x * cw, y = r.y * ch;
        // White outer ring
        ctx.beginPath();
        ctx.arc(x, y, 8 * dpr, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(255, 255, 255, 0.95)";
        ctx.fill();
        // Dark mid ring for contrast against white highlights
        ctx.beginPath();
        ctx.arc(x, y, 7 * dpr, 0, Math.PI * 2);
        ctx.strokeStyle = "rgba(2, 6, 23, 0.9)";
        ctx.lineWidth = 1 * dpr;
        ctx.stroke();
        // Coloured centre
        ctx.beginPath();
        ctx.arc(x, y, 5 * dpr, 0, Math.PI * 2);
        ctx.fillStyle = r.color;
        ctx.fill();
        drawLabelPill(ctx, x + 12 * dpr, y - 8 * dpr, r.lesionName, r.color, dpr);
      }
    }
  }
  /** Draw a high-contrast label pill: dark pill with a coloured
   *  left-edge stripe and white text. Anchored at (x, y) as the
   *  top-left of the pill. */
  function drawLabelPill(
    ctx: CanvasRenderingContext2D,
    x: number, y: number, text: string, color: string, dpr: number,
  ) {
    const fontSize = 11 * dpr;
    ctx.font = `600 ${fontSize}px JetBrains Mono, ui-monospace, monospace`;
    const padX = 5 * dpr;
    const padY = 3 * dpr;
    const stripeW = 3 * dpr;
    const metrics = ctx.measureText(text);
    const txtW = metrics.width;
    const pillH = fontSize + 2 * padY;
    const pillW = txtW + 2 * padX + stripeW;
    // Shift the anchor so the pill sits above (x, y)
    const px = x;
    const py = y - pillH;
    // Dark rounded rect background
    const r2 = 4 * dpr;
    ctx.beginPath();
    ctx.moveTo(px + r2, py);
    ctx.arcTo(px + pillW, py,          px + pillW, py + pillH, r2);
    ctx.arcTo(px + pillW, py + pillH,  px,         py + pillH, r2);
    ctx.arcTo(px,         py + pillH,  px,         py,         r2);
    ctx.arcTo(px,         py,          px + pillW, py,         r2);
    ctx.closePath();
    ctx.fillStyle = "rgba(2, 6, 23, 0.9)";
    ctx.fill();
    ctx.strokeStyle = "rgba(255, 255, 255, 0.25)";
    ctx.lineWidth   = 1 * dpr;
    ctx.stroke();
    // Coloured left stripe
    ctx.fillStyle = color;
    ctx.fillRect(px, py + 1 * dpr, stripeW, pillH - 2 * dpr);
    // Text
    ctx.fillStyle = "#F8FAFC";
    ctx.textBaseline = "middle";
    ctx.fillText(text, px + stripeW + padX, py + pillH / 2);
    ctx.textBaseline = "alphabetic";
  }

  // ---------------- Pointer drawing on overlay (Lesion mode) ----------------
  // The overlay only accepts pointer events in Lesion mode (see the JSX
  // `pointer-events-none` classList below). That means the Anatomy
  // flow can scrub the timeline freely without fighting the drawing
  // layer for wheel/pointer events.
  let drawing: null | { startX: number; startY: number } = null;
  function onOverlayDown(e: PointerEvent) {
    if (mode() !== "lesion") return;
    const r = overlayCvs.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width;
    const ny = (e.clientY - r.top)  / r.height;
    if (lesionTool() === "point") {
      addROI({ kind: "point", x: nx, y: ny });
      return;
    }
    // rect
    drawing = { startX: nx, startY: ny };
    overlayCvs.setPointerCapture(e.pointerId);
  }
  function onOverlayMove(e: PointerEvent) {
    if (!drawing || mode() !== "lesion" || lesionTool() !== "rect") return;
    const r = overlayCvs.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width;
    const ny = (e.clientY - r.top)  / r.height;
    drawOverlay();
    const ctx = overlayCvs.getContext("2d")!;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const cw = overlayCvs.width, ch = overlayCvs.height;
    const x = Math.min(drawing.startX, nx) * cw;
    const y = Math.min(drawing.startY, ny) * ch;
    const w = Math.abs(nx - drawing.startX) * cw;
    const h = Math.abs(ny - drawing.startY) * ch;
    const ghost = activeLesion()?.color ?? "#27a567";
    ctx.setLineDash([6, 4]);
    // Halo + coloured stroke so the ghost reads on white highlights
    // just like a committed ROI (see drawOverlay).
    ctx.strokeStyle = "rgba(2, 6, 23, 0.9)";
    ctx.lineWidth = 4 * dpr;
    ctx.strokeRect(x, y, w, h);
    ctx.strokeStyle = ghost;
    ctx.lineWidth = 2 * dpr;
    ctx.strokeRect(x, y, w, h);
    ctx.setLineDash([]);
  }
  function onOverlayUp(e: PointerEvent) {
    if (!drawing) return;
    try { overlayCvs.releasePointerCapture(e.pointerId); } catch {}
    const r = overlayCvs.getBoundingClientRect();
    const nx = (e.clientX - r.left) / r.width;
    const ny = (e.clientY - r.top)  / r.height;
    const x = Math.min(drawing.startX, nx);
    const y = Math.min(drawing.startY, ny);
    const w = Math.abs(nx - drawing.startX);
    const h = Math.abs(ny - drawing.startY);
    drawing = null;
    if (w < 0.01 || h < 0.01) return;
    addROI({ kind: "rect", x, y, w, h });
  }
  function addROI(base: { kind: "rect" | "point"; x: number; y: number; w?: number; h?: number }) {
    const l = activeLesion();
    if (!l) return;  // nothing to label against — should be unreachable
                     // because the default-picker effect seeds activeLesion
                     // as soon as the procedure list resolves.
    setROIs(produce((xs: ROI[]) => {
      xs.push({
        id: crypto.randomUUID(),
        frameTime: curTime(),
        kind: base.kind,
        x: base.x, y: base.y, w: base.w, h: base.h,
        lesionId: l.id, lesionName: l.name, color: l.color,
        include: true,
      });
    }));
    drawOverlay();
  }

  // ---------------- Still capture ----------------
  function captureStill() {
    if (!videoEl || !hiddenCvs) return;
    const w = videoEl.videoWidth || 640, h = videoEl.videoHeight || 480;
    hiddenCvs.width = w; hiddenCvs.height = h;
    const ctx = hiddenCvs.getContext("2d")!;
    ctx.drawImage(videoEl, 0, 0, w, h);
    const dataUrl = hiddenCvs.toDataURL("image/jpeg", 0.8);
    // Seed the label from the active mode's currently-selected
    // preset — anatomy mode picks up the anatomy label, lesion mode
    // picks up the lesion. The reader can override via the editor.
    const kind: "anatomy" | "lesion" = mode();
    const preset = kind === "anatomy" ? activeAnatomy() : activeLesion();
    const label = preset?.name ?? "";
    const color = (preset && "color" in preset && (preset as { color?: string }).color)
                    || (kind === "anatomy" ? "#27a567" : "#f59e0b");
    setStills(produce((xs: CapturedStill[]) => {
      xs.push({
        id: crypto.randomUUID(), frameTime: curTime(),
        dataUrl, captureAt: Date.now(), include: true,
        label, kind,
        presetId: preset?.id,
        color,
      });
    }));
  }
  /** Granular field update — writing a single path keeps the DOM
   *  input mounted so the user doesn't lose focus mid-typing. */
  function updateStillLabel(idx: number, value: string, presetId?: string, color?: string) {
    setStills(idx, produce((s: CapturedStill) => {
      s.label = value;
      s.presetId = presetId;
      if (color) s.color = color;
    }));
  }
  function toggleStillInclude(idx: number, v: boolean) {
    setStills(idx, "include", v);
  }
  function deleteStill(idx: number, id: string) {
    setStills(produce((xs: CapturedStill[]) => { xs.splice(idx, 1); }));
    if (editingStillId() === id) setEditingStillId(null);
  }

  // ---------------- Anatomy spans (Audacity-style range select) ----------------
  // Range is selected by click-dragging on the timeline. On release
  // we call `commitRange()` with the currently-active anatomy label,
  // mirroring Audacity's "pick a label track first, then drag" flow.
  function commitRange(range: { t0: number; t1: number }, label: AnatomyLabel | null) {
    if (!label) return;
    const tStart = Math.min(range.t0, range.t1);
    const tEnd   = Math.max(range.t0, range.t1);
    if (tEnd - tStart < 0.3) return;  // click, not drag
    setSpans((xs) => [...xs, {
      id: crypto.randomUUID(),
      labelId: label.id, labelName: label.name,
      tStart, tEnd,
      color: colorForSpan(xs.length),
    }]);
  }
  // ---------------- Span editing (right-panel inline) ----------------
  // Clamp a new time to the video duration and to non-inverted order
  // (start < end). The caller may pass partial {tStart?, tEnd?} —
  // the other side is held.
  function updateSpan(id: string, patch: Partial<AnatomySpan>) {
    setSpans((xs) => xs.map((s) => {
      if (s.id !== id) return s;
      const next = { ...s, ...patch };
      if (patch.labelId) {
        const al = anatomyList().find((a) => a.id === patch.labelId);
        if (al) { next.labelId = al.id; next.labelName = al.name; }
      }
      // Guard: enforce 0 ≤ tStart < tEnd ≤ duration(), min 0.1 s wide.
      const d = duration() || next.tEnd;
      next.tStart = Math.max(0, Math.min(d, next.tStart));
      next.tEnd   = Math.max(0, Math.min(d, next.tEnd));
      if (next.tEnd - next.tStart < 0.1) next.tEnd = Math.min(d, next.tStart + 0.1);
      return next;
    }));
  }
  function deleteSpan(id: string) {
    setSpans((xs) => xs.filter((s) => s.id !== id));
    if (editingSpanId() === id) setEditingSpanId(null);
  }
  function deleteROI(id: string) {
    setROIs(produce((xs: ROI[]) => {
      const i = xs.findIndex((r) => r.id === id);
      if (i >= 0) xs.splice(i, 1);
    }));
  }
  // ROI label / preset edit — mirrors updateStillLabel for stills.
  // The "preset" parameters mean: when the user picks an item from
  // the Lesion-presets dropdown we copy its id + colour onto the ROI
  // so the canvas overlay re-tints to match. Free-typed labels keep
  // the previous colour so the chip stays readable.
  // CRITICAL: mutate via produce on the matching index, NOT by
  // building a new array with map(). See the createStore comment at
  // the rois declaration for why — keystroke-causes-focus-loss is
  // a regression the user has surfaced twice.
  function updateROILabel(id: string, name: string, lesionId?: string, color?: string) {
    setROIs(
      (r: ROI) => r.id === id,
      produce((r: ROI) => {
        r.lesionName = name;
        if (lesionId !== undefined) r.lesionId = lesionId;
        if (color !== undefined) r.color = color;
      }),
    );
    drawOverlay();
  }
  function toggleROIInclude(id: string, on: boolean) {
    setROIs(
      (r: ROI) => r.id === id,
      produce((r: ROI) => { r.include = on; }),
    );
  }
  // Editor open-state for the ROI list — single ROI editable at a
  // time, mirroring CapturedStills' editingStillId.
  const [editingROIId, setEditingROIId] = createSignal<string | null>(null);

  // ---------------- Keyboard shortcuts ----------------
  //   Space        play/pause
  //   ← / →        step one frame (Shift = one second)
  //   1 / 2 / 4    speed 0.5× / 1× / 2×
  //   A            switch to Anatomy mode
  //   L            switch to Lesion mode (+ auto-pause)
  //   C            capture still
  //   R            lesion rectangle tool (only effective in Lesion mode)
  //   .            lesion point tool (Lesion mode)
  //   Esc          cancel in-progress range drag or overlay draw
  const kh = (e: KeyboardEvent) => {
    if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement || e.target instanceof HTMLSelectElement) return;
    if (e.key === " ")          { e.preventDefault(); togglePlay(); return; }
    if (e.key === "ArrowLeft")  { e.preventDefault(); step(e.shiftKey ? -1 : -1 / 30); return; }
    if (e.key === "ArrowRight") { e.preventDefault(); step(e.shiftKey ?  1 :  1 / 30); return; }
    if (e.key === "1") { setSpeed(0.5); return; }
    if (e.key === "2") { setSpeed(1); return; }
    if (e.key === "4") { setSpeed(2); return; }
    if (e.key === "a" || e.key === "A") { setMode("anatomy"); return; }
    if (e.key === "l" || e.key === "L") { setMode("lesion");  return; }
    if (e.key === "c" || e.key === "C") { captureStill(); return; }
    if (e.key === "r") { if (mode() === "lesion") setLesionTool("rect"); return; }
    if (e.key === ".") { if (mode() === "lesion") setLesionTool("point"); return; }
    if (e.key === "Escape") { setRangeSel(null); drawing = null; drawOverlay(); return; }
  };
  if (typeof window !== "undefined") {
    window.addEventListener("keydown", kh);
    onCleanup(() => window.removeEventListener("keydown", kh));
  }

  function togglePlay() { if (!videoEl) return; videoEl.paused ? videoEl.play() : videoEl.pause(); }
  function step(ds: number) { if (!videoEl) return; videoEl.currentTime = Math.max(0, Math.min(duration(), videoEl.currentTime + ds)); }
  function seek(t: number)  { if (!videoEl) return; videoEl.currentTime = Math.max(0, Math.min(duration(), t)); }

  // ---------------- Timeline pointer model ----------------
  //
  // Three separate interactive zones stack on the timeline:
  //
  //   1. Playhead handle (the ball on the green stick) — drags
  //      the *current playback time* only. Never touches labels.
  //   2. Span bar middle — click to select/open editor.
  //      Span bar edges (6 px left + 6 px right) — drag to resize
  //      `tStart` / `tEnd` of that span.
  //   3. Timeline background (anywhere not covered by 1 or 2) —
  //      drag to create a new span (Audacity-style). Plain click
  //      (no drag) seeks the playhead.
  //
  // Each zone's pointerdown handler stops propagation after claiming
  // the drag, so a drag on a span edge never falls through to the
  // background's create logic.
  type DragOp =
    | { kind: "create";  startT: number; pointerId: number }
    | { kind: "scrub";   pointerId: number }
    | { kind: "resize";  spanId: string; edge: "start" | "end"; pointerId: number };
  let timelineDrag: DragOp | null = null;
  /** Track whether a pointerdown ever became a drag; used by the
   *  background "plain click = seek" fallback so an accidental
   *  zero-width drag still behaves like a click. */
  let backgroundClickDistance = 0;

  function timelineTimeFromClientX(clientX: number): number {
    const r = timelineEl.getBoundingClientRect();
    const frac = Math.max(0, Math.min(1, (clientX - r.left) / r.width));
    return frac * duration();
  }
  function timelineTimeFromEvent(e: PointerEvent): number {
    return timelineTimeFromClientX(e.clientX);
  }
  /** Background (empty-area) pointerdown → begin create-drag. */
  function onTimelineDown(e: PointerEvent) {
    if (!duration()) return;
    const t = timelineTimeFromEvent(e);
    timelineDrag = { kind: "create", startT: t, pointerId: e.pointerId };
    backgroundClickDistance = 0;
    timelineEl.setPointerCapture(e.pointerId);
    // Seed a zero-width ghost. We *don't* seek here — the playhead
    // should stay put while the user drags out a new span; a plain
    // click (no drag) seeks on pointerup instead.
    setRangeSel({ t0: t, t1: t });
  }
  /** Playhead handle pointerdown → begin scrub-drag. */
  function onPlayheadDown(e: PointerEvent) {
    if (!duration()) return;
    e.stopPropagation();
    timelineDrag = { kind: "scrub", pointerId: e.pointerId };
    timelineEl.setPointerCapture(e.pointerId);
    // Seek immediately so grabbing the handle feels responsive.
    seek(timelineTimeFromEvent(e));
  }
  /** Span-edge pointerdown → begin resize-drag. Edge is which end
   *  of the span the handle represents. */
  function onSpanEdgeDown(e: PointerEvent, spanId: string, edge: "start" | "end") {
    if (!duration()) return;
    e.stopPropagation();
    timelineDrag = { kind: "resize", spanId, edge, pointerId: e.pointerId };
    timelineEl.setPointerCapture(e.pointerId);
    // Keep the row highlighted while resizing so the user sees which
    // span they're editing without having to look at the right panel.
    setEditingSpanId(spanId);
  }
  function onTimelineMove(e: PointerEvent) {
    if (!timelineDrag) return;
    const t = timelineTimeFromEvent(e);
    if (timelineDrag.kind === "create") {
      backgroundClickDistance += Math.abs(e.movementX);
      setRangeSel({ t0: timelineDrag.startT, t1: t });
      return;
    }
    if (timelineDrag.kind === "scrub") {
      seek(t);
      return;
    }
    // resize
    if (timelineDrag.edge === "start") updateSpan(timelineDrag.spanId, { tStart: t });
    else                                updateSpan(timelineDrag.spanId, { tEnd: t });
  }
  function onTimelineUp(e: PointerEvent) {
    if (!timelineDrag) return;
    try { timelineEl.releasePointerCapture(timelineDrag.pointerId); } catch {}
    if (timelineDrag.kind === "create") {
      const rel = rangeSel();
      const width = rel ? Math.abs(rel.t1 - rel.t0) : 0;
      if (width < 0.3 && backgroundClickDistance < 4) {
        // Pure click (no drag) — seek to the click point. Using the
        // stored startT ensures we don't accidentally seek to a stale
        // pointermove location outside the timeline bounds.
        seek(timelineDrag.startT);
      } else if (width >= 0.3 && mode() === "anatomy" && rel) {
        commitRange(rel, activeAnatomy());
      }
      setRangeSel(null);
    }
    timelineDrag = null;
  }

  // ---------------- Finalise + PDF export ----------------
  /** Fetch a (same-origin, token-bearing) image URL and inline it as a
   *  base64 data URL so the print window renders it without a second
   *  authenticated round-trip racing window.print(). */
  async function urlToDataUrl(u: string): Promise<string> {
    try {
      const r = await fetch(u);
      if (!r.ok) return "";
      const blob = await r.blob();
      return await new Promise<string>((res) => {
        const fr = new FileReader();
        fr.onload = () => res(String(fr.result || ""));
        fr.onerror = () => res("");
        fr.readAsDataURL(blob);
      });
    } catch { return ""; }
  }

  async function exportPDF() {
    const rep = report();
    const prof = profile();
    const draft = autoDraft();
    const includeStills = stills.filter((s) => s.include);
    const includeROIs   = rois.filter((r) => r.include !== false);

    // Resolve the auto-draft photo grid to inline base64 (one frame per
    // distinct lesion). Done before building the HTML so the print
    // window never races an authenticated image load.
    const photoCards = draft
      ? (await Promise.all(draft.photos.map(async (ph) => {
          const data = await urlToDataUrl(photoUrl(ph.instance_uid));
          if (!data) return "";
          const loc = ph.landmark ? ` <span style="color:#94a3b8;">(${escapeHtml(ph.landmark)})</span>` : "";
          return `<figure style="margin:0;break-inside:avoid;">
            <img src="${data}" style="width:100%;max-width:100%;border:1px solid #cbd5e1;border-radius:4px;" />
            <figcaption style="font-size:11px;color:#334155;margin-top:4px;text-align:center;">${escapeHtml(ph.name)}${loc}</figcaption>
          </figure>`;
        }))).filter(Boolean)
      : [];

    // Letterhead from the user's settings Profile (hospital logo +
    // department). Falls back gracefully when unset.
    const logo = prof.orgLogoDataUrl
      ? `<img src="${prof.orgLogoDataUrl}" style="height:56px;max-width:200px;object-fit:contain;" />` : "";
    const orgName = prof.orgName ? escapeHtml(prof.orgName) : "";
    const orgDept = prof.orgDepartment ? escapeHtml(prof.orgDepartment) : "";
    const orgContact = [prof.orgAddress, prof.orgPhone, prof.orgEmail].filter(Boolean).map(escapeHtml).join(" · ");
    const letterhead = (logo || orgName || orgDept) ? `
      <header style="display:flex;align-items:center;gap:16px;border-bottom:2px solid #27a567;padding-bottom:12px;margin-bottom:8px;">
        ${logo}
        <div>
          ${orgName ? `<div style="font-size:18px;font-weight:700;color:#0f172a;">${orgName}</div>` : ""}
          ${orgDept ? `<div style="font-size:13px;color:#475569;">${orgDept}</div>` : ""}
          ${orgContact ? `<div style="font-size:10px;color:#94a3b8;margin-top:2px;">${orgContact}</div>` : ""}
        </div>
      </header>` : "";

    // Procedure(s) — bullets when more than one was labelled.
    const PROC_LBL: Record<string, string> = { egd: "Esophagogastroduodenoscopy (EGD)", colonoscopy: "Colonoscopy" };
    const procList = draft && draft.procedures.length
      ? `<ul>${draft.procedures.map((x) => `<li>${escapeHtml(PROC_LBL[x] ?? x)}</li>`).join("")}</ul>`
      : `<p>${escapeHtml(procedure())}</p>`;
    const landmarkList = draft && draft.landmarks.length
      ? `<ul class="landmark-list">${draft.landmarks.map((l) =>
          `<li>${l.ticked ? "☑" : "☐"} ${escapeHtml(l.name)}</li>`).join("")}</ul>`
      : "";

    const html = `<!doctype html><html><head><title>Endoscopy report</title>
      <style>
        body{font-family:system-ui,-apple-system,Segoe UI,sans-serif;color:#0f172a;padding:32px;max-width:820px;margin:0 auto;}
        h1{border-bottom:2px solid #27a567;padding-bottom:6px;}
        h2{margin-top:24px;color:#0f7a49;}
        .meta td{padding:3px 10px 3px 0;font-size:12px;}
        .meta th{text-align:left;font-weight:600;color:#475569;padding:3px 10px 3px 0;font-size:12px;}
        pre{white-space:pre-wrap;background:#f1f5f9;padding:10px;border-radius:4px;font-family:inherit;}
        .anatomy-list li{margin:4px 0;}
        .landmark-list{list-style:none;padding:0;columns:2;font-size:13px;}
        .landmark-list li{margin:3px 0;break-inside:avoid;}
        .photo-grid{display:grid;grid-template-columns:repeat(3,1fr);gap:12px;margin-top:8px;}
        img{max-width:240px;border:1px solid #cbd5e1;margin:4px;}
        .photo-grid img{margin:0;}
        .stills{display:flex;flex-wrap:wrap;gap:8px;}
        footer{margin-top:40px;color:#94a3b8;font-size:11px;border-top:1px solid #e2e8f0;padding-top:8px;}
      </style></head><body>
      ${letterhead}
      <h1>Endoscopy report${orgName ? "" : " — " + escapeHtml(procedure())}</h1>
      <table class="meta">
        <tr><th>Patient</th><td>${escapeHtml(p.patientName ?? "—")}</td>
            <th>MRN</th><td>${escapeHtml(p.patientId ?? "—")}</td></tr>
        <tr><th>Study UID</th><td colspan="3" style="font-family:monospace;font-size:10px;">${escapeHtml(p.studyUid)}</td></tr>
        <tr><th>Date</th><td>${new Date().toISOString().slice(0,10)}</td>
            <th>Procedure</th><td>${escapeHtml(procedure())}</td></tr>
      </table>
      ${draft ? `<h2>Procedure</h2>${procList}` : ""}
      <h2>Procedure notes</h2><pre>${escapeHtml(rep.procedureNotes || "—")}</pre>
      ${landmarkList ? `<h2>Landmark coverage</h2>${landmarkList}` : `
      <h2>Anatomical landmarks reached</h2>
      <ul class="anatomy-list">
        ${spans().map((s) =>
          `<li><strong>${fmtTime(s.tStart)}–${fmtTime(s.tEnd)}</strong>
           · ${escapeHtml(s.labelName)}</li>`).join("") || "<li>—</li>"}
      </ul>`}
      <h2>Findings</h2><pre>${escapeHtml(rep.findings || "—")}</pre>
      <h2>Diagnosis</h2><pre>${escapeHtml(rep.diagnosis || "—")}</pre>
      <h2>Complications</h2><pre>${escapeHtml(rep.complications || "None reported")}</pre>
      <h2>Recommendations</h2><pre>${escapeHtml(rep.recommendations || "—")}</pre>
      ${photoCards.length ? `<h2>Photographic findings (${photoCards.length})</h2>
        <div class="photo-grid">${photoCards.join("")}</div>` : ""}
      ${includeStills.length ? `<h2>Captured images (${includeStills.length})</h2>
        <div class="stills">${includeStills.map((s) => {
          const chip = s.label
            ? `<span style="display:inline-block;padding:1px 6px;border-radius:9999px;background:${s.color || "#64748b"};color:#fff;font-weight:600;margin-right:4px;">${escapeHtml(s.label)}</span>`
            : "";
          return `<figure><img src="${s.dataUrl}" />
            <figcaption style="font-size:10px;color:#64748b;">
              <span style="text-transform:uppercase;color:#94a3b8;font-size:9px;">${s.kind}</span>
              · t=${fmtTime(s.frameTime)}${chip ? " — " + chip : ""}
            </figcaption></figure>`;
        }).join("")}</div>` : ""}
      ${includeROIs.length ? `<h2>Regions of interest (${includeROIs.length})</h2>
        <table style="width:100%;border-collapse:collapse;font-size:11px;">
          <thead><tr>
            <th style="text-align:left;padding:4px 8px;border-bottom:1px solid #cbd5e1;color:#475569;">#</th>
            <th style="text-align:left;padding:4px 8px;border-bottom:1px solid #cbd5e1;color:#475569;">t</th>
            <th style="text-align:left;padding:4px 8px;border-bottom:1px solid #cbd5e1;color:#475569;">kind</th>
            <th style="text-align:left;padding:4px 8px;border-bottom:1px solid #cbd5e1;color:#475569;">label</th>
            <th style="text-align:left;padding:4px 8px;border-bottom:1px solid #cbd5e1;color:#475569;">location (% of frame)</th>
          </tr></thead>
          <tbody>${includeROIs.map((r, i) => {
            const pct = (v: number | undefined) => v == null ? "" : `${(v * 100).toFixed(1)}%`;
            const loc = r.kind === "rect"
              ? `x=${pct(r.x)} y=${pct(r.y)} w=${pct(r.w)} h=${pct(r.h)}`
              : `x=${pct(r.x)} y=${pct(r.y)}`;
            const chip = `<span style="display:inline-block;padding:1px 6px;border-radius:9999px;background:${r.color};color:#fff;font-weight:600;">${escapeHtml(r.lesionName)}</span>`;
            return `<tr>
              <td style="padding:4px 8px;border-bottom:1px solid #e2e8f0;color:#94a3b8;">${i + 1}</td>
              <td style="padding:4px 8px;border-bottom:1px solid #e2e8f0;font-family:monospace;">${fmtTime(r.frameTime)}</td>
              <td style="padding:4px 8px;border-bottom:1px solid #e2e8f0;text-transform:uppercase;font-size:9px;color:#64748b;">${r.kind}</td>
              <td style="padding:4px 8px;border-bottom:1px solid #e2e8f0;">${chip}</td>
              <td style="padding:4px 8px;border-bottom:1px solid #e2e8f0;font-family:monospace;font-size:10px;color:#475569;">${loc}</td>
            </tr>`;
          }).join("")}</tbody>
        </table>` : ""}
      <footer>Generated by VaultPod Endoscopy Viewer — ${new Date().toISOString()}</footer>
      <script>window.onload=()=>setTimeout(()=>window.print(), 300);</script>
      </body></html>`;
    const w = window.open("", "_blank", "width=900,height=1000");
    if (w) { w.document.write(html); w.document.close(); }
  }

  return (
    <div class="relative flex h-full min-h-0 w-full bg-ink-0">
      {/* ========== Left tray ========== */}
      <Show when={!trayOpen()}>
        <button class="absolute left-0 top-1/2 z-10 -translate-y-1/2 rounded-r border border-l-0 border-ink-300 bg-ink-100/95 px-1 py-3 text-[11px] text-ink-700 backdrop-blur hover:bg-ink-200 hover:text-white"
                title="Show case tray"
                onClick={() => setTrayOpen(true)}>»</button>
      </Show>
      <aside class={trayOpen()
        ? "flex w-56 shrink-0 flex-col border-r border-ink-300 bg-ink-100 hidden md:flex"
        : "hidden"}>
        {/* Folder-tree header */}
        <div class="flex items-center justify-between border-b border-ink-300 px-2.5 py-2 text-[10px] uppercase tracking-wider text-ink-700">
          <span class="flex items-center gap-1.5">
            <span class="h-3.5 w-3.5 text-brand"><IconScope /></span>
            <span>endoscopy</span>
          </span>
          <button class="text-[12px] text-ink-700 hover:text-white"
                  title="Collapse"
                  onClick={() => setTrayOpen(false)}>«</button>
        </div>
        {/* Single scrollable tree mirroring pathopod's PathologyShell
            structure: case (with patient + MRN + procedure inline) →
            series (with one row per instance, active highlighted). */}
        <div class="flex-1 overflow-auto px-1.5 py-2 text-[11px]">
          <TreeBranch open={caseOpen()} setOpen={setCaseOpen}
                      icon={<IconCase />}
                      label={<><span class="text-ink-700">case</span>
                               {/* Case ID is ALWAYS present (derived from
                                   the study UID), so an anonymised case
                                   with no name/MRN is still identifiable. */}
                               <span class="ml-1.5 truncate font-mono text-white">{caseId(p.studyUid)}</span></>}>
            <div class="mx-2 mb-2 space-y-1">
              <div class="flex gap-2">
                <span class="w-16 text-[10px] uppercase text-ink-700">case id</span>
                <span class="truncate font-mono font-medium text-white" title={p.studyUid}>{caseId(p.studyUid)}</span>
              </div>
              <div class="flex gap-2">
                <span class="w-16 text-[10px] uppercase text-ink-700">patient</span>
                <span class="truncate font-medium text-white">{p.patientName || "Anonymous"}</span>
              </div>
              <div class="flex gap-2">
                <span class="w-16 text-[10px] uppercase text-ink-700">mrn</span>
                <span class="truncate text-ink-900">{p.patientId || "Unknown"}</span>
              </div>
              <div class="flex gap-2">
                <span class="w-16 text-[10px] uppercase text-ink-700">procedure</span>
                <span class="text-ink-900">{procedure()}</span>
              </div>
            </div>
            <TreeBranch open={seriesTreeOpen()} setOpen={setSeriesTreeOpen}
                        icon={<IconSlide />}
                        label={<><span class="text-ink-700">series</span>
                                 <span class="ml-1.5 font-mono text-white">{seriesGroups().length}</span></>}>
              {/* DICOM Explorer pattern: one row per Series, with a
                  kind-distinct preview — contact-sheet thumbnail for
                  the stills series, play-button glyph for the video
                  series. Mirrors Weasis / RadiAnt / Karl Storz AIDA
                  conventions for mixed VL Endoscopic + Video
                  Endoscopic studies (issue #4 ask 4). */}
              <div class="mx-2 mb-1 space-y-1.5">
                <For each={seriesGroups()}>{(g) => {
                  const activeSeries = () => g.seriesUid === p.activeInstance?.seriesUid;
                  const isStills = g.kind === "stills";
                  const isVideo  = g.kind === "video";
                  const firstInst = g.instances[0];
                  const tok = () => auth()?.accessToken ?? "";
                  const thumbUrl = () => {
                    if (!firstInst) return "";
                    const qs = tok() ? `?access_token=${encodeURIComponent(tok())}` : "";
                    return isStills
                      ? `/viewer-api/image/studies/${firstInst.studyUid}/series/${firstInst.seriesUid}/instances/${firstInst.instanceUid}.jpg${qs}`
                      : "";
                  };
                  return (
                    <div>
                    <button type="button"
                            class="block w-full rounded border bg-ink-200 p-1.5 text-left transition hover:border-brand"
                            classList={{
                              "border-brand bg-brand/15 text-white": activeSeries(),
                              "border-ink-300": !activeSeries(),
                            }}
                            title={`${g.description || g.kind} · S${g.seriesNumber} · ${g.instances.length} instance${g.instances.length === 1 ? "" : "s"}`}
                            onClick={() => pickSeries(g)}>
                      <div class="flex items-center gap-2">
                        {/* kind-distinct preview */}
                        <Show when={isStills} fallback={
                          <Show when={isVideo} fallback={
                            <span class="grid h-12 w-16 place-items-center rounded bg-black text-[10px] uppercase text-ink-700">SR</span>
                          }>
                            <span class="relative grid h-12 w-16 place-items-center rounded bg-black">
                              <span class="text-2xl text-brand">▶</span>
                              <span class="absolute bottom-0.5 right-1 font-mono text-[9px] text-white/80">
                                {g.instances.length} clip{g.instances.length === 1 ? "" : "s"}
                              </span>
                            </span>
                          </Show>
                        }>
                          <span class="relative grid h-12 w-16 place-items-center overflow-hidden rounded bg-black">
                            <img src={thumbUrl()}
                                 class="h-full w-full object-cover opacity-90"
                                 loading="lazy"
                                 alt="snapshots" />
                            <span class="absolute bottom-0.5 right-1 rounded bg-black/70 px-1 font-mono text-[9px] text-white">
                              {g.instances.length}
                            </span>
                          </span>
                        </Show>
                        <div class="min-w-0 flex-1">
                          <div class="flex items-center gap-1.5">
                            <span class="chip-brand !text-[9px]">
                              {isStills ? "STILLS" : isVideo ? "VIDEO" : (g.modality || "—")}
                            </span>
                            <span class="text-[10px] text-ink-700">S{g.seriesNumber}</span>
                          </div>
                          <div class="mt-0.5 truncate text-[11px] leading-snug">
                            {g.description || (isStills ? "Endoscopy Snapshots" : isVideo ? "Endoscopy Video" : "Series")}
                          </div>
                        </div>
                      </div>
                    </button>
                    {/* Multiple clips in one video series — each instance
                        is individually selectable so all of them are
                        reachable (the series row alone only opens the
                        first). The video reloads on activeInstance change. */}
                    <Show when={isVideo && g.instances.length > 1}>
                      <div class="ml-3 mt-1 space-y-0.5 border-l border-ink-300 pl-2">
                        <For each={g.instances}>{(inst, i) => {
                          const activeClip = () => inst.instanceUid === p.activeInstance?.instanceUid;
                          return (
                            <button type="button"
                                    class="flex w-full items-center gap-1.5 rounded px-1.5 py-0.5 text-left text-[10px] transition"
                                    classList={{ "bg-brand/20 text-white": activeClip(), "text-ink-700 hover:text-white": !activeClip() }}
                                    title={`Clip ${i() + 1} · instance #${inst.instanceNumber}`}
                                    onClick={() => p.onPickInstance?.(inst.instanceUid)}>
                              <span class="text-brand">▶</span>
                              <span>Clip {i() + 1}</span>
                              <span class="ml-auto font-mono text-ink-700">#{inst.instanceNumber}</span>
                            </button>
                          );
                        }}</For>
                      </div>
                    </Show>
                    </div>
                  );
                }}</For>
                <Show when={seriesGroups().length === 0}>
                  <div class="px-1 py-1.5 text-[10px] text-ink-700">no series</div>
                </Show>
              </div>
            </TreeBranch>
          </TreeBranch>
        </div>
      </aside>

      {/* ========== Centre: video OR stills ========== */}
      {/* The centre column branches on the SOP class of the active
          instance. Stills (VL Endoscopic Image) and Video (Video
          Endoscopic Image) live in the same Study but render in
          completely different viewports, so we mount one or the
          other rather than stacking both. The reader switches with
          the series tree on the left. Issue #4 ask 1. */}
      <div class="relative flex min-w-0 flex-1 flex-col">
        <Show when={activeKind() === "stills" && stillsGroup()}>
          {(g) => (
            <StillsView studyUid={p.studyUid}
                        instances={g().instances}
                        activeInstanceUid={p.activeInstance?.instanceUid}
                        onPickInstance={(uid) => p.onPickInstance?.(uid)}
                        onSaveDraft={doSaveDraft}
                        saveState={saveState} />
          )}
        </Show>
        <Show when={activeKind() !== "stills"}>
        <div ref={videoBox} class="relative flex min-h-0 flex-1 items-center justify-center bg-black">
          {/* Video element is always mounted so that MSE can attach
              to it as soon as the createResource runs; for the
              blob-URL fallback, src is bound reactively. The "mse:ok"
              `videoUrl()` resolves to the server-extracted MP4 URL
              (with token in query) and the browser does native
              progressive playback via byte-range requests. */}
          <video ref={(el) => attachVideo(el as HTMLVideoElement)}
                 class="max-h-full max-w-full"
                 src={videoUrl() ?? undefined}
                 preload="auto"
                 playsinline muted
                 controls={false} />
          <Show when={!videoUrl()}>
            <div class="pointer-events-none absolute inset-0 flex items-center justify-center text-sm text-ink-700">
              Loading video…
            </div>
          </Show>
          <canvas ref={overlayCvs}
                  class="absolute inset-0 h-full w-full"
                  classList={{
                    "pointer-events-auto cursor-crosshair": mode() === "lesion",
                    "pointer-events-none":                  mode() === "anatomy",
                  }}
                  onPointerDown={onOverlayDown}
                  onPointerMove={onOverlayMove}
                  onPointerUp={onOverlayUp} />
          <canvas ref={hiddenCvs} class="hidden" />

          {/* Mode-context hint bottom-left on the video, kept brief so
              it doesn't obscure the mucosa. Shown once the video has
              a src (either blob URL or MSE sentinel). */}
          <Show when={!!videoUrl()}>
            <div class="pointer-events-none absolute bottom-2 left-2 rounded bg-black/60 px-2 py-1 text-[10px] font-mono text-white/85 shadow">
              <Show when={mode() === "anatomy"} fallback={
                <span>LESION · pick label ▸ drag on frame — <b class="text-warn">video paused</b></span>
              }>
                <span>ANATOMY · pick label ▸ drag on timeline to mark a range</span>
              </Show>
            </div>
          </Show>

          {/* Loading pill — shown centre-top when the browser is
              waiting for video data (initial buffer, seek into an
              unbuffered region, or network stall). Keeps the user
              informed instead of staring at a frozen frame.
              Also shown during the initial DICOM fetch (before the
              video element can report state). */}
          <Show when={fetchProgress() !== null && fetchProgress()! < 1}>
            <div class="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded-full border border-brand/70 bg-black/80 px-3 py-1 font-mono text-[10px] text-white/90 shadow">
              <span class="inline-block h-2 w-2 animate-pulse rounded-full bg-brand align-middle" />
              <span class="ml-2 align-middle">
                downloading video
                <Show when={fetchTotalBytes()}>
                  {" "}<span class="text-brand">
                    {Math.round(fetchProgress()! * 100)}% · {(fetchTotalBytes()! / 1e6).toFixed(1)} MB
                  </span>
                </Show>
              </span>
            </div>
          </Show>
          <Show when={videoLoading() && fetchProgress() === 1}>
            <div class="pointer-events-none absolute left-1/2 top-3 -translate-x-1/2 rounded-full border border-brand/70 bg-black/80 px-3 py-1 font-mono text-[10px] text-white/90 shadow">
              <span class="inline-block h-2 w-2 animate-pulse rounded-full bg-brand align-middle" />
              <span class="ml-2 align-middle">buffering…</span>
            </div>
          </Show>
        </div>

        {/* ===== Procedure picker (drives the anatomy vocabulary) =====
            Solid's `value={expr}` on <select> can desync when the
            <option> children mount from a <For> AFTER the value is
            written — the select falls back to the first option and
            the signal update doesn't re-apply. Using a ref + effect
            that writes `el.value` after every render guarantees the
            displayed option matches the signal. */}
        <div class="flex flex-wrap items-center gap-2 border-t border-ink-300 bg-ink-100 px-3 py-1.5 text-[11px]">
          <span class="text-[10px] uppercase tracking-wider text-ink-700">procedure</span>
          <select class="rounded border border-ink-300 bg-ink-50 px-1.5 py-0.5 text-[11px] text-ink-900 focus:border-brand focus:outline-none"
                  ref={(el) => { createEffect(() => { el.value = procedure(); }); }}
                  onChange={(e) => {
                    setManualProc(true);
                    setProcedure((e.currentTarget as HTMLSelectElement).value as ProcedureType);
                  }}>
            <For each={PROCEDURE_TYPES}>{(pt) => (
              <option value={pt}>{PROCEDURE_LABEL[pt]}</option>
            )}</For>
          </select>
          <span class="text-[10px] text-ink-700">· {anatomyList().length} landmarks</span>
        </div>

        {/* ===== Transport bar (stable across modes) ===== */}
        <div class="flex flex-wrap items-center gap-2 border-t border-ink-300 bg-ink-100 px-3 py-2 text-[11px]">
          <button class="inline-flex h-7 w-9 items-center justify-center rounded bg-brand text-white hover:bg-brand-dark"
                  title="Play / pause (Space)"
                  onClick={togglePlay}>
            <span class="h-4 w-4">{playing() ? <IconPause /> : <IconPlay />}</span>
          </button>
          <button class="inline-flex h-7 w-7 items-center justify-center rounded border border-ink-300 bg-ink-50 text-ink-800 hover:text-white hover:bg-ink-200"
                  title="Step -1 frame (←)" onClick={() => step(-1 / 30)}>
            <span class="h-4 w-4"><IconStepBack /></span>
          </button>
          <button class="inline-flex h-7 w-7 items-center justify-center rounded border border-ink-300 bg-ink-50 text-ink-800 hover:text-white hover:bg-ink-200"
                  title="Step +1 frame (→)" onClick={() => step( 1 / 30)}>
            <span class="h-4 w-4"><IconStepFwd /></span>
          </button>
          <div class="ml-2 flex items-center gap-1 text-[10px] text-ink-700">
            <For each={SPEEDS}>{(v) => (
              <button class="rounded border border-ink-300 px-1.5 py-0.5"
                      classList={{ "bg-brand text-white border-brand": speed() === v }}
                      onClick={() => setSpeed(v)}>
                {v}×
              </button>
            )}</For>
          </div>
          <div class="ml-3 font-mono text-[11px] text-ink-900">
            {fmtTime(curTime())} <span class="text-ink-700">/</span> {fmtTime(duration())}
          </div>
          <button class="ml-auto inline-flex h-7 items-center gap-1.5 rounded border border-ink-300 bg-ink-50 px-2 text-[11px] text-ink-800 hover:text-white hover:bg-ink-200"
                  onClick={captureStill} title="Capture still (C)">
            <span class="h-4 w-4"><IconCamera /></span>
            <span>Still</span>
          </button>
        </div>

        {/* ===== Mode tabs + per-mode picker ===== */}
        <div class="flex flex-wrap items-center gap-2 border-t border-ink-300 bg-ink-50 px-3 py-2 text-[11px]">
          <div class="inline-flex overflow-hidden rounded border border-ink-300">
            <button class="inline-flex items-center gap-1.5 px-3 py-1 text-[11px] font-medium transition"
                    classList={{
                      "bg-brand text-white":                            mode() === "anatomy",
                      "bg-ink-100 text-ink-800 hover:bg-ink-200":       mode() !== "anatomy",
                    }}
                    title="Anatomy — drag timeline range to label (A)"
                    onClick={() => setMode("anatomy")}>
              <span class="h-4 w-4"><IconAnatomy /></span>
              <span>Anatomy</span>
            </button>
            <button class="inline-flex items-center gap-1.5 border-l border-ink-300 px-3 py-1 text-[11px] font-medium transition"
                    classList={{
                      "bg-brand text-white":                            mode() === "lesion",
                      "bg-ink-100 text-ink-800 hover:bg-ink-200":       mode() !== "lesion",
                    }}
                    title="Lesion — pause + draw bbox on a frame (L)"
                    onClick={() => setMode("lesion")}>
              <span class="h-4 w-4"><IconLesion /></span>
              <span>Lesion</span>
            </button>
          </div>

          <Show when={mode() === "anatomy"} fallback={
            <>
              {/* Lesion picker + tool + active-label preview */}
              <span class="ml-2 text-[10px] uppercase tracking-wider text-ink-700">lesion label</span>
              <select class="rounded border border-ink-300 bg-ink-100 px-1.5 py-0.5 text-[11px] text-ink-900 focus:border-brand focus:outline-none"
                      value={activeLesion()?.id ?? ""}
                      onChange={(e) => {
                        const id = (e.currentTarget as HTMLSelectElement).value;
                        const l = lesionList().find((x) => x.id === id);
                        if (l) setActiveLesion(l);
                      }}>
                <For each={lesionList()}>{(l) => (
                  <option value={l.id}>{l.name}</option>
                )}</For>
              </select>
              <span class="inline-block h-3 w-3 rounded-sm border border-ink-300"
                    style={{ background: activeLesion()?.color ?? "transparent" }}
                    title={activeLesion()?.name} />
              <span class="ml-2 text-[10px] uppercase tracking-wider text-ink-700">tool</span>
              <button class="inline-flex items-center gap-1 rounded border border-ink-300 px-1.5 py-0.5 text-[10px]"
                      classList={{ "bg-brand text-white border-brand": lesionTool() === "rect" }}
                      title="Bounding box (R)" onClick={() => setLesionTool("rect")}>
                <span class="h-3.5 w-3.5"><IconBbox /></span>
                <span>box</span>
              </button>
              <button class="inline-flex items-center gap-1 rounded border border-ink-300 px-1.5 py-0.5 text-[10px]"
                      classList={{ "bg-brand text-white border-brand": lesionTool() === "point" }}
                      title="Point marker (.)" onClick={() => setLesionTool("point")}>
                <span class="h-3.5 w-3.5"><IconKeypoint /></span>
                <span>point</span>
              </button>
              <span class="ml-auto text-[10px] text-ink-700">video paused · drag on frame to label</span>
            </>
          }>
            {/* Anatomy picker */}
            <span class="ml-2 text-[10px] uppercase tracking-wider text-ink-700">landmark</span>
            <select class="rounded border border-ink-300 bg-ink-100 px-1.5 py-0.5 text-[11px] text-ink-900 focus:border-brand focus:outline-none"
                    value={activeAnatomy()?.id ?? ""}
                    onChange={(e) => {
                      const id = (e.currentTarget as HTMLSelectElement).value;
                      const a = anatomyList().find((x) => x.id === id);
                      if (a) setActiveAnatomy(a);
                    }}>
              <For each={anatomyList()}>{(a) => (
                <option value={a.id}>{a.name}</option>
              )}</For>
            </select>
            <span class="ml-auto text-[10px] text-ink-700">
              drag on timeline below to mark a range · click seeks
            </span>
          </Show>
        </div>

        {/* ===== Timeline — tall, click-seek + Audacity-style drag-select ===== */}
        <div class="border-t border-ink-300 bg-ink-100 px-3 py-3">
          <div class="mb-1 flex items-center justify-between text-[10px] uppercase tracking-wider text-ink-700">
            <span>timeline</span>
            <span class="font-mono text-ink-900">
              {fmtTime(curTime())} / {fmtTime(duration())}
            </span>
          </div>

          {/* ============ Video loading strip ============
              Thick enough (5 px) to read at a glance, positioned
              immediately above the Audacity timeline so the eye
              can compare playhead against buffered/loaded range.
              Three stacked layers share the SAME horizontal scale
              as the timeline below (0 .. duration):
                · grey track       — total video duration (always)
                · blue download    — fraction of bytes received from
                                     the backend. For a 1 h clip
                                     streamed over range requests
                                     this grows ahead of the playhead
                                     as the browser prefetches.
                · green buffered   — video.buffered TimeRanges —
                                     what the <video> element can
                                     play right now without stalling.
              A numeric caption on the right shows "downloaded /
              total · duration" so the user has a concrete number. */}
          <div class="mb-0.5 flex items-center justify-between text-[9px] font-mono text-ink-700">
            <Show when={fetchProgress() !== null && fetchProgress()! < 1}
                  fallback={<span class="text-brand">loaded</span>}>
              <Show when={fetchTotalBytes()} fallback={
                <span>downloading video…</span>
              }>
                <span>
                  <span class="text-sky-400">{Math.round((fetchProgress() ?? 0) * 100)}%</span>
                  {" · "}
                  {((fetchProgress() ?? 0) * (fetchTotalBytes() ?? 0) / 1e6).toFixed(1)} / {(fetchTotalBytes()! / 1e6).toFixed(1)} MB
                </span>
              </Show>
            </Show>
            <span>duration <span class="text-ink-900">{fmtTime(duration())}</span></span>
          </div>
          <div class="relative mb-1 h-[5px] overflow-hidden rounded-sm bg-ink-300/70">
            {/* Indeterminate state — Content-Length unknown; show a
                slow left-to-right pulse so the user sees activity. */}
            <Show when={fetchProgress() !== null && fetchProgress()! < 1 && !fetchTotalBytes()}>
              <div class="absolute top-0 h-full w-1/4 bg-sky-500/80"
                   style={{ animation: "vpdIndeterminate 1.6s linear infinite" }} />
            </Show>
            {/* Determinate fetch fill — byte fraction of the DICOM. */}
            <Show when={fetchProgress() !== null && fetchTotalBytes()}>
              <div class="absolute top-0 h-full bg-sky-500/80 transition-[width] duration-150"
                   style={{ width: `${Math.round((fetchProgress() ?? 0) * 100)}%` }} />
            </Show>
            {/* video.buffered — decoded, playable ranges.
                Percent math is inlined into the style getter so
                Solid re-runs it whenever `duration()` changes — the
                previous `const d = duration()` captured the value
                at first render (0) and the spans stacked at 100%
                when duration finally resolved. */}
            <For each={bufferedRanges()}>{([bs, be]) => (
              <div class="absolute top-0 h-full bg-brand"
                   style={{
                     left:  `${(bs / (duration() || 1)) * 100}%`,
                     width: `${Math.max(0.5, ((be - bs) / (duration() || 1)) * 100)}%`,
                   }}
                   title={`buffered · ${fmtTime(bs)}–${fmtTime(be)}`} />
            )}</For>
            {/* Live playhead tick, aligned with the timeline below
                so the user sees if the playhead has caught up with
                the buffered edge (the "buffering…" pill appears in
                the viewport when it does). */}
            <Show when={duration() > 0}>
              <div class="pointer-events-none absolute top-[-2px] h-[9px] w-[2px] -translate-x-1/2 bg-white shadow"
                   style={{ left: `${(curTime() / (duration() || 1)) * 100}%` }} />
            </Show>
          </div>
          <div ref={timelineEl}
               class="relative h-9 select-none rounded border border-ink-300 bg-ink-200"
               classList={{
                 "cursor-col-resize": mode() === "anatomy",
                 "cursor-pointer":    mode() === "lesion",
               }}
               onPointerDown={onTimelineDown}
               onPointerMove={onTimelineMove}
               onPointerUp={onTimelineUp}
               onPointerCancel={onTimelineUp}>
            {/* Tick marks every 5 seconds so the user has a visual
                frame of reference even before any span is committed. */}
            <Show when={duration() > 0}>
              <For each={Array.from({ length: Math.floor(duration() / 5) + 1 }, (_, i) => i * 5)}>{(t) => (
                <div class="pointer-events-none absolute top-0 h-2 w-px bg-ink-400"
                     style={{ left: `${(t / duration()) * 100}%` }}>
                  <span class="absolute left-1 top-1.5 text-[8px] font-mono text-ink-700">
                    {fmtTime(t)}
                  </span>
                </div>
              )}</For>
            </Show>

            {/* Committed anatomy spans.
                Each span is drawn as three sibling elements on top of
                the timeline background:
                  · a middle <button> — click to select/open editor,
                    stopPropagation so the click doesn't seek.
                  · a left edge <div> — 6 px wide, ew-resize cursor,
                    pointerdown starts a tStart-resize drag.
                  · a right edge <div> — same but for tEnd. */}
            <For each={spans()}>{(s) => {
              // Percentages must be evaluated reactively against the
              // CURRENT `duration()` — otherwise spans loaded from
              // the server before video.loadedmetadata fires stack
              // at the right edge because `duration()` was still 0
              // at For-render time. Hence the inline getters.
              const leftPct  = () => (s.tStart / (duration() || 1)) * 100;
              const widthPct = () => ((s.tEnd - s.tStart) / (duration() || 1)) * 100;
              const selected = () => editingSpanId() === s.id;
              return (
                <>
                  <button type="button"
                          class="absolute bottom-0 h-5 rounded-sm text-left transition"
                          style={{
                            left:  `${leftPct()}%`,
                            width: `${widthPct()}%`,
                            background: s.color + "d0",
                            "box-shadow": selected() ? "0 0 0 2px #f8fafc inset, 0 0 0 3px #27a567" : undefined,
                          }}
                          title={`${s.labelName} · ${fmtTime(s.tStart)}–${fmtTime(s.tEnd)} — click to edit · drag edges to resize`}
                          onPointerDown={(e) => e.stopPropagation()}
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingSpanId(selected() ? null : s.id);
                          }}>
                    <span class="pointer-events-none absolute left-1 top-0 truncate text-[9px] font-medium text-white"
                          style={{ "max-width": "calc(100% - 4px)" }}>
                      {s.labelName}
                    </span>
                  </button>
                  {/* Resize handles — rendered on top of the span bar.
                      Kept narrow so the middle of the bar is easy to
                      click for "select", and widened-hit with padding
                      via cursor hint. */}
                  <div class="absolute bottom-0 h-5 cursor-ew-resize rounded-l-sm"
                       style={{
                         left:  `calc(${leftPct()}% - 3px)`,
                         width: "8px",
                         background: "rgba(248, 250, 252, 0.35)",
                         "border-left": "2px solid rgba(248, 250, 252, 0.9)",
                       }}
                       title={`Drag to adjust start (${fmtTime(s.tStart)})`}
                       onPointerDown={(e) => onSpanEdgeDown(e, s.id, "start")} />
                  <div class="absolute bottom-0 h-5 cursor-ew-resize rounded-r-sm"
                       style={{
                         left:  `calc(${leftPct() + widthPct()}% - 5px)`,
                         width: "8px",
                         background: "rgba(248, 250, 252, 0.35)",
                         "border-right": "2px solid rgba(248, 250, 252, 0.9)",
                       }}
                       title={`Drag to adjust end (${fmtTime(s.tEnd)})`}
                       onPointerDown={(e) => onSpanEdgeDown(e, s.id, "end")} />
                </>
              );
            }}</For>

            {/* Lesion pins — small diamond at the frame time so the
                reader can scrub back to a drawn ROI. Percent is an
                inline getter so it re-runs when duration() resolves
                (same ex-bug as spans above). */}
            <For each={rois}>{(r) => (
              <div class="pointer-events-none absolute top-0 h-3 w-3 -translate-x-1/2 rotate-45 border border-white/90"
                   style={{
                     left: `${(r.frameTime / (duration() || 1)) * 100}%`,
                     background: r.color,
                   }}
                   title={`${r.lesionName} @ ${fmtTime(r.frameTime)}`} />
            )}</For>

            {/* Live range-selection ghost while the user drags */}
            <Show when={rangeSel() && Math.abs(rangeSel()!.t1 - rangeSel()!.t0) >= 0.05}>
              <div class="pointer-events-none absolute top-0 bottom-0 rounded-sm border border-brand bg-brand/25"
                   style={{
                     left:  `${(Math.min(rangeSel()!.t0, rangeSel()!.t1) / (duration() || 1)) * 100}%`,
                     width: `${(Math.abs(rangeSel()!.t1 - rangeSel()!.t0) / (duration() || 1)) * 100}%`,
                   }}>
                <span class="absolute bottom-1 left-1 text-[9px] font-medium text-white">
                  {activeAnatomy()?.name ?? "(pick label)"} · {fmtTime(Math.abs(rangeSel()!.t1 - rangeSel()!.t0))}
                </span>
              </div>
            </Show>

            {/* Playhead — the green stick sits visually on top of the
                whole timeline (pointer-events:none so it doesn't block
                clicks on spans), and the ball on top is a separate,
                draggable handle (pointer-events:auto). Scrubbing the
                playhead NEVER creates or moves a label — it only
                changes the video's current time, letting the user
                move playback to a point before or after any existing
                span without accidentally labeling. */}
            <div class="pointer-events-none absolute top-0 z-20 h-full w-[2px] -translate-x-1/2 bg-brand shadow"
                 style={{ left: `${(curTime() / (duration() || 1)) * 100}%` }} />
            <div class="absolute -top-2 z-30 h-5 w-5 -translate-x-1/2 cursor-grab rounded-full border border-white/60 bg-brand shadow-lg active:cursor-grabbing hover:scale-110"
                 style={{
                   left: `${(curTime() / (duration() || 1)) * 100}%`,
                   transition: timelineDrag?.kind === "scrub" ? "none" : "transform 120ms",
                 }}
                 title={`Playhead · ${fmtTime(curTime())} — drag to scrub`}
                 onPointerDown={onPlayheadDown} />
          </div>
        </div>
        </Show>{/* /video branch */}
      </div>

      {/* ========== Right dock: report + stills ========== */}
      <Show when={!panelOpen()}>
        <button class="absolute right-0 top-1/2 z-10 -translate-y-1/2 rounded-l border border-r-0 border-ink-300 bg-ink-100/95 px-1 py-3 text-[11px] text-ink-700 backdrop-blur hover:bg-ink-200 hover:text-white"
                title="Show report panel"
                onClick={() => setPanelOpen(true)}>«</button>
      </Show>
      {/* Vertical splitter — drag to resize the report panel. Mounted
          as a sibling of the panel, immediately to its left, so the
          drag affects panel width directly. Hidden when the panel is
          collapsed. */}
      <Show when={panelOpen()}>
        <div class="w-1 shrink-0 cursor-col-resize bg-ink-300/60 hover:bg-brand/60 hidden md:block"
             title="Drag to resize report panel"
             onPointerDown={(ev) => {
               ev.preventDefault();
               const startX = ev.clientX;
               const startW = reportWidth();
               (ev.currentTarget as HTMLElement).setPointerCapture(ev.pointerId);
               const move = (e: PointerEvent) => {
                 // Drag right grows panel? No — panel is on the RIGHT
                 // edge, so drag-LEFT (negative delta from startX)
                 // grows it; drag-RIGHT shrinks it.
                 const next = Math.max(280, Math.min(720, startW - (e.clientX - startX)));
                 setReportWidth(next);
               };
               const up = (e: PointerEvent) => {
                 (ev.currentTarget as HTMLElement).releasePointerCapture?.(e.pointerId);
                 window.removeEventListener("pointermove", move);
                 window.removeEventListener("pointerup", up);
               };
               window.addEventListener("pointermove", move);
               window.addEventListener("pointerup", up);
             }} />
      </Show>
      <aside class={panelOpen()
        ? "flex shrink-0 flex-col border-l border-ink-300 bg-ink-100 hidden md:flex"
        : "hidden"}
             style={panelOpen() ? `width: ${reportWidth()}px` : ""}>
        <div class="flex items-center justify-between border-b border-ink-300 px-3 py-2 text-[11px] uppercase tracking-wider text-ink-700">
          <span>report draft</span>
          <button class="text-[12px] text-ink-700 hover:text-white"
                  title="Collapse"
                  onClick={() => setPanelOpen(false)}>»</button>
        </div>
        <div class="flex-1 overflow-auto p-3 space-y-3 text-[11px]">

          {/* Automate drafting — roll the per-image labels (procedure,
              landmark, lesion) up into the report draft below + a
              structured preview that flows into the PDF. */}
          <button class="inline-flex w-full items-center justify-center gap-2 rounded border border-brand bg-brand/15 px-3 py-2 text-[11px] font-medium text-brand transition hover:bg-brand hover:text-white disabled:opacity-50"
                  title="Build the report draft from the labels you placed on the still images"
                  disabled={autoBusy()}
                  onClick={doAutomateDraft}>
            <span class="h-4 w-4"><IconScope /></span>
            {autoBusy() ? "Drafting…" : "Automate drafting"}
          </button>

          <Show when={autoDraft()}>
            {(d) => (
              <div class="rounded border border-ink-300 bg-ink-200/50 p-2 space-y-2">
                <div class="text-[10px] uppercase tracking-wider text-ink-700">auto-draft preview</div>

                {/* Procedure(s) */}
                <div>
                  <div class="text-[10px] uppercase text-ink-700">procedure</div>
                  <Show when={d().procedures.length} fallback={<div class="text-ink-700">— not labelled —</div>}>
                    <ul class="ml-3 list-disc text-ink-900">
                      <For each={d().procedures}>{(x) => <li>{x === "egd" ? "Esophagogastroduodenoscopy (EGD)" : "Colonoscopy"}</li>}</For>
                    </ul>
                  </Show>
                </div>

                {/* Landmark coverage */}
                <Show when={d().landmarks.length}>
                  <div>
                    <div class="text-[10px] uppercase text-ink-700">landmark coverage</div>
                    <ul class="grid grid-cols-2 gap-x-2">
                      <For each={d().landmarks}>{(l) => (
                        <li classList={{ "text-ink-900": l.ticked, "text-ink-700": !l.ticked }}>
                          <span class="mr-1">{l.ticked ? "☑" : "☐"}</span>{l.name}
                        </li>
                      )}</For>
                    </ul>
                  </div>
                </Show>

                {/* Findings */}
                <div>
                  <div class="text-[10px] uppercase text-ink-700">findings</div>
                  <Show when={d().findings.length} fallback={<div class="text-ok">Normal — no abnormality labelled.</div>}>
                    <ul class="ml-3 list-disc text-ink-900">
                      <For each={d().findings}>{(f) => (
                        <li>{f.name}<Show when={f.locations.length}><span class="text-ink-700"> ({f.locations.join(", ")})</span></Show></li>
                      )}</For>
                    </ul>
                  </Show>
                </div>

                {/* Photo grid — one frame per distinct lesion */}
                <Show when={d().photos.length}>
                  <div>
                    <div class="text-[10px] uppercase text-ink-700">photos ({d().photos.length})</div>
                    <div class="grid grid-cols-3 gap-1.5">
                      <For each={d().photos}>{(ph) => (
                        <figure class="m-0">
                          <img src={photoUrl(ph.instance_uid)} class="h-16 w-full rounded border border-ink-300 object-cover" alt={ph.name} />
                          <figcaption class="mt-0.5 truncate text-center text-[9px] text-ink-700" title={`${ph.name}${ph.landmark ? " · " + ph.landmark : ""}`}>{ph.name}</figcaption>
                        </figure>
                      )}</For>
                    </div>
                  </div>
                </Show>
                <p class="text-[9px] text-ink-700">Review + edit the narrative below, then Save draft / Export PDF.</p>
              </div>
            )}
          </Show>

          {/* Anatomy log first — it's the primary labeling artifact
              and needs to be visible without scrolling past five
              free-text fields. Each row exposes edit + delete
              buttons DIRECTLY (not hidden behind a mode), plus an
              expandable inline editor for precise time adjustment. */}
          <div>
            <div class="mb-1 flex items-center justify-between text-[10px] uppercase text-ink-700">
              <span>anatomy log ({spans().length})</span>
              <span class="text-[9px] normal-case text-ink-700">click pencil to adjust · trash to delete</span>
            </div>
            <ul class="space-y-1 rounded border border-ink-300 bg-ink-200 p-1.5">
              <Show when={spans().length > 0} fallback={
                <li class="px-1 py-0.5 text-[10px] text-ink-700">
                  no spans yet — drag on the timeline in Anatomy mode
                </li>
              }>
                <For each={spans()}>{(s) => (
                  <li class="rounded border border-transparent px-1 py-0.5 text-[10px] transition hover:border-ink-300 hover:bg-ink-100"
                      classList={{ "border-brand bg-brand/10": editingSpanId() === s.id }}>
                    {/* Collapsed summary row — always has visible
                        edit + delete icon buttons on the right so the
                        feature is discoverable without a click. */}
                    <div class="flex items-center gap-1.5">
                      <button class="inline-block h-2.5 w-2.5 rounded-sm ring-1 ring-black/40"
                              style={{ background: s.color }}
                              title="Seek to span start"
                              onClick={() => seek(s.tStart)} />
                      <button class="font-mono text-ink-700 hover:text-white"
                              title="Seek to span start"
                              onClick={() => seek(s.tStart)}>
                        {fmtTime(s.tStart)}–{fmtTime(s.tEnd)}
                      </button>
                      <span class="truncate text-ink-900">{s.labelName}</span>
                      <button class="ml-auto inline-flex h-5 w-5 items-center justify-center rounded border border-ink-300 bg-ink-100 text-ink-700 hover:text-white hover:bg-brand hover:border-brand"
                              title={editingSpanId() === s.id ? "Close editor" : "Edit label + times"}
                              onClick={() => setEditingSpanId(editingSpanId() === s.id ? null : s.id)}>
                        <span class="h-3.5 w-3.5"><IconEdit /></span>
                      </button>
                      <button class="inline-flex h-5 w-5 items-center justify-center rounded border border-ink-300 bg-ink-100 text-ink-700 hover:text-white hover:bg-danger hover:border-danger"
                              title="Delete span"
                              onClick={() => deleteSpan(s.id)}>
                        <span class="h-3.5 w-3.5"><IconTrash /></span>
                      </button>
                    </div>
                    {/* Expanded editor — only shown when the row is
                        selected. Label select + start/end inputs. */}
                    <Show when={editingSpanId() === s.id}>
                      <div class="mt-1.5 space-y-1.5 border-t border-ink-300 px-0.5 pt-1.5">
                        <div class="flex items-center gap-1.5">
                          <span class="text-[9px] uppercase tracking-wider text-ink-700 w-10">label</span>
                          <select class="flex-1 rounded border border-ink-300 bg-ink-100 px-1 py-0.5 text-[10px] text-ink-900 focus:border-brand focus:outline-none"
                                  value={s.labelId}
                                  onChange={(e) => updateSpan(s.id, { labelId: (e.currentTarget as HTMLSelectElement).value })}>
                            <For each={anatomyList()}>{(a) => (
                              <option value={a.id}>{a.name}</option>
                            )}</For>
                          </select>
                        </div>
                        <div class="flex items-center gap-1.5">
                          <span class="text-[9px] uppercase tracking-wider text-ink-700 w-10">times</span>
                          <input type="number" step="0.1" min="0" max={duration() || undefined}
                                 class="w-16 rounded border border-ink-300 bg-ink-100 px-1 py-0.5 text-right font-mono text-[10px] text-ink-900 focus:border-brand focus:outline-none"
                                 value={s.tStart.toFixed(1)}
                                 onChange={(e) => {
                                   const v = parseFloat((e.currentTarget as HTMLInputElement).value);
                                   if (Number.isFinite(v)) updateSpan(s.id, { tStart: v });
                                 }} />
                          <span class="text-[9px] text-ink-700">→</span>
                          <input type="number" step="0.1" min="0" max={duration() || undefined}
                                 class="w-16 rounded border border-ink-300 bg-ink-100 px-1 py-0.5 text-right font-mono text-[10px] text-ink-900 focus:border-brand focus:outline-none"
                                 value={s.tEnd.toFixed(1)}
                                 onChange={(e) => {
                                   const v = parseFloat((e.currentTarget as HTMLInputElement).value);
                                   if (Number.isFinite(v)) updateSpan(s.id, { tEnd: v });
                                 }} />
                          <span class="text-[9px] text-ink-700">s</span>
                          <button class="ml-auto inline-flex h-5 items-center gap-1 rounded border border-brand bg-brand/15 px-2 text-[10px] text-brand hover:bg-brand hover:text-white"
                                  onClick={() => setEditingSpanId(null)}>done</button>
                        </div>
                      </div>
                    </Show>
                  </li>
                )}</For>
              </Show>
            </ul>
          </div>

          <ReportField label="Procedure notes"
                       value={report().procedureNotes}
                       onChange={(v) => setReport({ ...report(), procedureNotes: v })} />
          <ReportField label="Findings"
                       value={report().findings}
                       onChange={(v) => setReport({ ...report(), findings: v })} />
          <ReportField label="Diagnosis"
                       value={report().diagnosis}
                       onChange={(v) => setReport({ ...report(), diagnosis: v })} />
          <ReportField label="Complications"
                       value={report().complications}
                       onChange={(v) => setReport({ ...report(), complications: v })} />
          <ReportField label="Recommendations"
                       value={report().recommendations}
                       onChange={(v) => setReport({ ...report(), recommendations: v })} />

          <div>
            <div class="mb-1 flex items-center justify-between text-[10px] uppercase text-ink-700">
              <span>captured stills ({stills.length})</span>
              <span class="text-[9px] normal-case">▣ include · ✎ label · ✕ remove</span>
            </div>
            <div class="space-y-2">
              {/* Index (not For) keeps per-row DOM stable even when
                  setStills replaces an entry with a new object
                  reference — critical so the label input doesn't
                  lose focus on every keystroke. */}
              <Index each={stills}>{(sGet, i) => {
                const s   = () => sGet();
                const isEditing = () => editingStillId() === s().id;
                // Pick the preset vocabulary based on the kind the
                // still was captured under — anatomy for landmarks,
                // lesion for lesions. The dropdown is only a
                // shortcut; the text input is the source of truth.
                const presetList = () => s().kind === "anatomy" ? anatomyList() : lesionList();
                return (
                  <div class="rounded border border-ink-300 bg-ink-200 p-1.5">
                    <div class="flex gap-2">
                      <div class="relative shrink-0">
                        <img src={s().dataUrl}
                             class="block h-20 w-20 cursor-pointer rounded object-cover ring-1 ring-ink-300 transition hover:ring-brand"
                             title={`Captured at ${fmtTime(s().frameTime)} — click to seek`}
                             onClick={() => seek(s().frameTime)} />
                        <span class="pointer-events-none absolute bottom-0.5 left-0.5 rounded bg-black/70 px-1 font-mono text-[9px] text-white">
                          {fmtTime(s().frameTime)}
                        </span>
                      </div>
                      <div class="flex min-w-0 flex-1 flex-col gap-1">
                        {/* Label chip — single unified field. The
                            tiny kind-badge on the left marks which
                            vocabulary it came from. */}
                        <div class="flex flex-wrap items-center gap-1">
                          <span class="inline-flex shrink-0 items-center rounded-sm border border-ink-300 bg-ink-100 px-1 text-[9px] uppercase tracking-wider"
                                classList={{
                                  "text-brand":  s().kind === "anatomy",
                                  "text-warn":   s().kind === "lesion",
                                }}>
                            {s().kind}
                          </span>
                          <Show when={s().label} fallback={
                            <span class="text-[10px] italic text-ink-700">no label</span>
                          }>
                            <span class="inline-flex min-w-0 items-center gap-1 rounded-full px-1.5 py-0.5 text-[10px] font-medium text-white"
                                  style={{ background: s().color ?? "#64748b" }}>
                              <span class="inline-block h-1.5 w-1.5 shrink-0 rounded-full bg-white/80" />
                              <span class="truncate">{s().label}</span>
                            </span>
                          </Show>
                        </div>
                        <div class="mt-auto flex items-center gap-1">
                          <label class="inline-flex items-center gap-1 text-[10px] text-ink-900">
                            <input type="checkbox"
                                   class="h-3 w-3"
                                   checked={s().include}
                                   onChange={(e) => toggleStillInclude(i, (e.currentTarget as HTMLInputElement).checked)} />
                            <span>in PDF</span>
                          </label>
                          <button class="ml-auto inline-flex h-5 w-5 items-center justify-center rounded border border-ink-300 bg-ink-100 text-ink-700 hover:text-white hover:bg-brand hover:border-brand"
                                  title={isEditing() ? "Close label editor" : "Edit label"}
                                  onClick={() => setEditingStillId(isEditing() ? null : s().id)}>
                            <span class="h-3.5 w-3.5"><IconEdit /></span>
                          </button>
                          <button class="inline-flex h-5 w-5 items-center justify-center rounded border border-ink-300 bg-ink-100 text-ink-700 hover:text-white hover:bg-danger hover:border-danger"
                                  title="Remove captured still"
                                  onClick={() => deleteStill(i, s().id)}>
                            <span class="h-3.5 w-3.5"><IconTrash /></span>
                          </button>
                        </div>
                      </div>
                    </div>
                    {/* Inline editor — single label field with a
                        quick-pick preset dropdown. Typing in the
                        text input updates `label` immediately,
                        clearing `presetId` since it's no longer a
                        canonical preset. Selecting from the preset
                        dropdown fills the input + re-assigns the
                        preset id + colour. */}
                    <Show when={isEditing()}>
                      <div class="mt-1.5 space-y-1 border-t border-ink-300 pt-1.5">
                        <div class="flex items-center gap-1.5">
                          <span class="w-12 text-[9px] uppercase tracking-wider text-ink-700">label</span>
                          <input type="text"
                                 class="flex-1 rounded border border-ink-300 bg-ink-100 px-1.5 py-0.5 text-[10px] text-ink-900 focus:border-brand focus:outline-none"
                                 placeholder={s().kind === "anatomy"
                                   ? "Landmark name (e.g. Antrum · A3)"
                                   : "Lesion name (e.g. Polyp — hepatic flexure)"}
                                 value={s().label}
                                 onInput={(e) => {
                                   // Clear presetId: the label is no
                                   // longer coming from a preset once
                                   // the user types. Colour stays
                                   // (last preset colour keeps the
                                   // chip readable) until a new
                                   // preset is picked.
                                   const v = (e.currentTarget as HTMLInputElement).value;
                                   updateStillLabel(i, v, undefined, undefined);
                                 }} />
                        </div>
                        <div class="flex items-center gap-1.5">
                          <span class="w-12 text-[9px] uppercase tracking-wider text-ink-700">preset</span>
                          <select class="flex-1 rounded border border-ink-300 bg-ink-100 px-1 py-0.5 text-[10px] text-ink-900 focus:border-brand focus:outline-none"
                                  value={s().presetId ?? ""}
                                  onChange={(e) => {
                                    const id = (e.currentTarget as HTMLSelectElement).value;
                                    if (!id) { updateStillLabel(i, s().label, undefined, undefined); return; }
                                    const item = presetList().find((x) => x.id === id) as AnatomyLabel | LesionLabel | undefined;
                                    if (!item) return;
                                    const col = "color" in item ? (item as LesionLabel).color
                                              : (s().kind === "anatomy" ? "#27a567" : "#f59e0b");
                                    updateStillLabel(i, item.name, item.id, col);
                                  }}>
                            <option value="">— pick from list —</option>
                            <For each={presetList()}>{(item) => (
                              <option value={item.id}>{item.name}</option>
                            )}</For>
                          </select>
                          <button class="inline-flex h-5 items-center rounded border border-brand bg-brand/15 px-2 text-[10px] text-brand hover:bg-brand hover:text-white"
                                  onClick={() => setEditingStillId(null)}>done</button>
                        </div>
                      </div>
                    </Show>
                  </div>
                );
              }}</Index>
            </div>
          </div>

          <div>
            <div class="mb-1 flex items-baseline justify-between text-[10px] uppercase text-ink-700">
              <span>ROIs ({rois.length})</span>
              <span class="normal-case text-ink-700">{rois.filter((r) => r.include !== false).length} in PDF</span>
            </div>
            <ul class="space-y-0.5 rounded border border-ink-300 bg-ink-200 p-1.5">
              <Show when={rois.length > 0} fallback={
                <li class="px-1 py-0.5 text-[10px] text-ink-700">draw a box or point in Lesion mode</li>
              }>
                <For each={rois}>{(r) => {
                  const isEditing = () => editingROIId() === r.id;
                  return (
                    <li class="rounded px-1 py-0.5 text-[10px] hover:bg-ink-100">
                      <div class="flex items-center gap-2">
                        <button class="inline-block h-2 w-2 rounded-sm"
                                style={{ background: r.color }}
                                title="Seek to frame"
                                onClick={() => seek(r.frameTime)} />
                        <button class="font-mono text-ink-700 hover:text-white"
                                title="Seek to frame"
                                onClick={() => seek(r.frameTime)}>
                          {fmtTime(r.frameTime)}
                        </button>
                        <span class="truncate text-ink-900">{r.lesionName}</span>
                        <span class="text-[9px] uppercase text-ink-700">{r.kind}</span>
                        <label class="ml-auto inline-flex items-center gap-1 text-[10px] text-ink-900"
                               title="Include this ROI in the export PDF">
                          <input type="checkbox"
                                 class="h-3 w-3"
                                 checked={r.include !== false}
                                 onChange={(e) => toggleROIInclude(r.id, (e.currentTarget as HTMLInputElement).checked)} />
                          <span class="text-[9px] uppercase tracking-wider">PDF</span>
                        </label>
                        <button class="inline-flex h-4 w-4 items-center justify-center text-ink-700 hover:text-white"
                                title={isEditing() ? "Close label editor" : "Edit label"}
                                onClick={() => setEditingROIId(isEditing() ? null : r.id)}>
                          <span class="h-3.5 w-3.5"><IconEdit /></span>
                        </button>
                        <button class="inline-flex h-4 w-4 items-center justify-center text-ink-700 hover:text-danger"
                                title="Delete ROI"
                                onClick={() => deleteROI(r.id)}>
                          <span class="h-3.5 w-3.5"><IconTrash /></span>
                        </button>
                      </div>
                      {/* Inline editor — same pattern as CapturedStills:
                          free-typed name + Lesion-preset dropdown for
                          quick re-labelling. The colour follows the
                          preset (or stays put for free-typed labels). */}
                      <Show when={isEditing()}>
                        <div class="mt-1 space-y-1 border-t border-ink-300 pt-1 pl-3">
                          <div class="flex items-center gap-1.5">
                            <span class="w-12 text-[9px] uppercase tracking-wider text-ink-700">label</span>
                            <input type="text"
                                   class="flex-1 rounded border border-ink-300 bg-ink-100 px-1.5 py-0.5 text-[10px] text-ink-900 focus:border-brand focus:outline-none"
                                   placeholder="Lesion name (e.g. Polyp — hepatic flexure)"
                                   value={r.lesionName}
                                   onInput={(e) => updateROILabel(r.id, (e.currentTarget as HTMLInputElement).value)} />
                          </div>
                          <div class="flex items-center gap-1.5">
                            <span class="w-12 text-[9px] uppercase tracking-wider text-ink-700">preset</span>
                            <select class="flex-1 rounded border border-ink-300 bg-ink-100 px-1 py-0.5 text-[10px] text-ink-900 focus:border-brand focus:outline-none"
                                    value={r.lesionId}
                                    onChange={(e) => {
                                      const id = (e.currentTarget as HTMLSelectElement).value;
                                      const item = lesionList().find((x) => x.id === id);
                                      if (item) updateROILabel(r.id, item.name, item.id, item.color);
                                    }}>
                              <option value="">— pick from list —</option>
                              <For each={lesionList()}>{(item) => (
                                <option value={item.id}>{item.name}</option>
                              )}</For>
                            </select>
                            <button class="inline-flex h-5 items-center rounded border border-brand bg-brand/15 px-2 text-[10px] text-brand hover:bg-brand hover:text-white"
                                    onClick={() => setEditingROIId(null)}>done</button>
                          </div>
                        </div>
                      </Show>
                    </li>
                  );
                }}</For>
              </Show>
            </ul>
          </div>
        </div>

        {/* ===== Footer: status + action buttons =====
            Three states the buttons cycle through:
              draft     → [Save Draft] + [Sign + Finalize]
              finalized → [Amend]   (body read-only)
              amended   → [Save Draft] + [Sign + Finalize]  (same as draft, distinct audit trail) */}
        <div class="border-t border-ink-300 p-3">
          {/* Status row — badge + finalized-by / seeded-from meta */}
          <div class="mb-2 flex items-center gap-2 text-[10px]">
            <span class="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wider"
                  classList={{
                    "bg-ink-200 text-ink-800 ring-1 ring-ink-300": reportStatus() === "draft",
                    "bg-brand/15 text-brand ring-1 ring-brand":    reportStatus() === "finalized",
                    "bg-warn/15 text-warn ring-1 ring-warn":       reportStatus() === "amended",
                  }}>
              <Show when={reportStatus() === "finalized"}>
                <span class="h-3 w-3"><IconSign /></span>
              </Show>
              {reportStatus()}
            </span>
            <Show when={reportStatus() === "finalized" && finalizedAt()}>
              <span class="text-ink-700">
                by {finalizedBy()} · {new Date(finalizedAt()!).toLocaleString()}
              </span>
            </Show>
            <Show when={reportStatus() === "draft" && seededFrom()}>
              <span class="text-[9px] italic text-ink-700">seeded from {seededFrom()}</span>
            </Show>
            <span class="ml-auto text-[9px] font-mono text-ink-700">
              <Show when={saveState() === "saving"}><span class="text-brand">saving…</span></Show>
              <Show when={saveState() === "saved"}><span class="text-brand">✓ saved</span></Show>
              <Show when={saveState() === "error"}><span class="text-danger">save failed</span></Show>
            </span>
          </div>

          {/* Action buttons — shown based on the current status. */}
          <Show when={reportStatus() !== "finalized"} fallback={
            <button class="inline-flex w-full items-center justify-center gap-2 rounded border border-warn bg-warn/10 px-3 py-2 text-[11px] font-medium text-warn hover:bg-warn hover:text-white"
                    title="Unlock this finalized report for further edits (tracked as an amendment in the audit log)"
                    onClick={doAmend}>
              <span class="h-4 w-4"><IconEdit /></span>
              Amend report
            </button>
          }>
            <button class="inline-flex w-full items-center justify-center gap-2 rounded border border-ink-300 bg-ink-200 px-3 py-2 text-[11px] font-medium text-ink-900 hover:bg-ink-100"
                    title="Save a draft copy — can be re-opened later from any device"
                    onClick={doSaveDraft}
                    disabled={saveState() === "saving"}>
              <span class="h-4 w-4"><IconSave /></span>
              Save draft
            </button>
            <button class="mt-2 inline-flex w-full items-center justify-center gap-2 rounded bg-brand px-3 py-2 text-[11px] font-medium text-white hover:bg-brand-dark"
                    title="Sign + finalize — locks the report; amendments are audit-logged"
                    onClick={doFinalize}
                    disabled={saveState() === "saving"}>
              <span class="h-4 w-4"><IconSign /></span>
              Sign + finalize
            </button>
          </Show>
          <button class="mt-2 w-full rounded border border-ink-300 bg-ink-50 px-3 py-2 text-[11px] text-ink-900 hover:bg-ink-200"
                  onClick={exportPDF}>
            Export PDF
          </button>
        </div>
      </aside>
    </div>
  );
}

function ReportField(p: { label: string; value: string; onChange: (v: string) => void }) {
  return (
    <div>
      <div class="mb-0.5 text-[10px] uppercase text-ink-700">{p.label}</div>
      <textarea
        class="w-full resize-y rounded border border-ink-300 bg-ink-200 p-1.5 text-[11px] text-ink-900 focus:border-brand focus:outline-none"
        rows={3}
        value={p.value}
        onInput={(e) => p.onChange((e.currentTarget as HTMLTextAreaElement).value)} />
    </div>
  );
}

function fmtTime(s: number): string {
  if (!isFinite(s)) return "–:––";
  const m = Math.floor(s / 60);
  const ss = Math.floor(s - m * 60);
  return `${m}:${ss.toString().padStart(2, "0")}`;
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"]/g, (c) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" }[c]!));
}

/** Scan a raw DICOM byte stream for the encapsulated Pixel Data
 *  fragment. Returns offset + length of the first item payload. Not a
 *  full parser — it only looks for the Pixel Data tag followed by the
 *  Basic Offset Table item and the first fragment item. Good enough
 *  for instances produced by `scripts/mp4-to-endo-dicom.py`. */
function findFragment(buf: Uint8Array): { offset: number; length: number } | null {
  // Tag 7FE0,0010 encoded as 4 bytes LE: E0 7F 10 00
  const TAG = [0xE0, 0x7F, 0x10, 0x00];
  for (let i = 132; i < buf.length - 16; i++) {
    if (buf[i] === TAG[0] && buf[i+1] === TAG[1] && buf[i+2] === TAG[2] && buf[i+3] === TAG[3]) {
      // VR is OB (0x4F 0x42), followed by 2 reserved bytes + 4-byte length
      if (buf[i+4] === 0x4F && buf[i+5] === 0x42) {
        // Past VR + 2 reserved = i+8; 4-byte length = i+8..i+12; data starts i+12
        let p = i + 12;
        // Basic Offset Table item: tag FFFE,E000 + 4-byte length
        if (buf[p] === 0xFE && buf[p+1] === 0xFF && buf[p+2] === 0x00 && buf[p+3] === 0xE0) {
          const botLen = buf[p+4] | (buf[p+5] << 8) | (buf[p+6] << 16) | (buf[p+7] << 24);
          p += 8 + botLen;
          // First fragment item
          if (buf[p] === 0xFE && buf[p+1] === 0xFF && buf[p+2] === 0x00 && buf[p+3] === 0xE0) {
            const fragLen = buf[p+4] | (buf[p+5] << 8) | (buf[p+6] << 16) | (buf[p+7] << 24);
            return { offset: p + 8, length: fragLen };
          }
        }
      }
    }
  }
  return null;
}

/** TreeBranch — VSCode-style folder-tree node.
 *  A left-indented group with a chevron disclosure button. Used to
 *  consolidate the endoscopy shell's left tray into a single unified
 *  tree (case → series), mirroring pathopod's PathologyShell layout
 *  per the modality-independence rule (this is a copy, not an
 *  import — the two modality forks are not allowed to share runtime
 *  code, see CLAUDE.md). */
function TreeBranch(p: {
  open: boolean;
  setOpen: (v: boolean) => void;
  icon: JSX.Element;
  label: JSX.Element;
  children: JSX.Element;
}): JSX.Element {
  return (
    <div class="mb-0.5">
      <button type="button"
              class="group flex w-full items-center gap-1.5 rounded px-1 py-1 text-left hover:bg-ink-200"
              onClick={() => p.setOpen(!p.open)}>
        <span class="grid h-3 w-3 place-items-center text-ink-700 transition-transform"
              classList={{ "rotate-90": p.open }}>
          <IconChevronRight size={12} />
        </span>
        <span class="h-3.5 w-3.5 text-brand">{p.icon}</span>
        <span class="flex-1 text-[11px] leading-tight">{p.label}</span>
      </button>
      <Show when={p.open}>
        <div class="ml-3.5 border-l border-ink-300/50 pl-1 pt-0.5">
          {p.children}
        </div>
      </Show>
    </div>
  );
}
